import "./Invoice.css";
import "./Invoicefiles/Invoiceadditions.css";
import { useEffect, useMemo, useRef, useState,useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaFilePdf,
  FaCheckCircle,
  FaMoneyBillWave,
  FaClipboardList,
  FaTimes,
  FaCoins,
  FaHistory,
  FaReceipt,
  FaUserCircle,
  FaBuilding,
  FaWallet,
  FaBoxOpen,
  FaPauseCircle,
  FaUndoAlt,
} from "react-icons/fa";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { db } from "../../firebase/firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  // deleteDoc,
  arrayUnion,
  serverTimestamp,
  runTransaction,
  writeBatch,
} from "firebase/firestore";
import { useClientsStatusMap, resolveClientStatus } from "../../hooks/useClientsStatusMap";
import {
  getProjectRef,
  PROJECT_REF_BADGE_STYLE,
  resolveProjectNumber,
  formatProjectSeq,
} from "../../hooks/useClientsStatusMap";
import { useToast } from "../../components/Dashboard/Invoicefiles/Usetoast";
import ToastContainer from "../../components/Dashboard/Invoicefiles/Toastcontainer";
import usePagination from "../../hooks/usePagination";
import Pagination from "./Pagination";


// import ConfirmDialog from "../../components/Dashboard/Invoicefiles/Confirmdialog";

const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
const PROJECT_TYPE_OPTIONS = ["Residential", "Commercial", "Renovation", "Interiors"];

// Blank row used by the "Construction Expense Allocation" popup (formerly
// labelled "Allocate Current Payment"). This captures a day-to-day
// construction/material expense entry — it is NOT the Estimation Budget
// Plan and never touches the `budgetPlans` collection. It is also fully
// independent of payment sequencing: an expense entry can be logged at
// any time, regardless of which payment(s) have or haven't been recorded,
// and recording it is never a prerequisite for the client's next payment.
const EMPTY_ALLOCATION_ROW = {
  name: "",
  quantity: "",
  pricePerQty: "",
  supplier: "",
  purchaseDate: "",
  notes: "",
};

// ---------------------------------------------------------------------
// PDF generation constants (Feature 1 + Feature 2).
//
// COMPANY_ADDRESS mirrors the address used elsewhere across the app's
// professional/printed documents. Update this single constant if the
// registered company address ever changes — nothing else needs to be
// touched, since every generated PDF reads from here.
//
// LOGO_PATH points at the static asset in the CRA `public/` folder, so it
// is fetched at runtime and embedded into the PDF as a data URL — it is
// never bundled or duplicated anywhere else.
// ---------------------------------------------------------------------
const COMPANY_NAME = "Sarrathi Constructions";
const COMPANY_ADDRESS =
  "82, Jagannathan Salai, Anjugam Nagar,SRP Colony, Kolathur, Chennai - 600082.";
const LOGO_PATH = "/logo/company logo.jpeg";
const COMPANY_EMAIL = "build@sarrathiconstructions.com";
const COMPANY_PHONE = "98405 24873, 98407 98059";
const COMPANY_WEBSITE = "www.sarrathiconstructions.com";

// ---------------------------------------------------------------------
// Shared PDF visual language — used identically by BOTH generated PDFs
// (individual invoice + full report) so styling can never drift between
// them. Only presentation lives here; no business logic.
// ---------------------------------------------------------------------
const PDF_THEME = {
  colors: {
    primaryDark: [23, 32, 51],   // headings / titles
    primary: [22, 37, 61],      // table header fill
    accentGold: [199, 156, 68], // divider line under header / section rules
    textMuted: [110, 118, 132],
    textBody: [55, 65, 81],
    borderLight: [225, 228, 233],
    rowStripe: [246, 247, 250],
    balanceRed: [196, 78, 66],
    paidGreen: [46, 138, 89],
  },
  fontFamily: "helvetica",
};

// Loads the company logo as a base64 data URL so jsPDF can embed it.
// Read-only, local static asset fetch — no Firestore access involved.
const loadCompanyLogoDataUrl = async () => {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) throw new Error("Logo not found");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Failed to load company logo for PDF:", err);
    return null;
  }
};

// Turns a client name into a safe, non-generic PDF filename, e.g.
// "Rahul Kumar" -> "Rahul_Kumar.pdf".
const sanitizeFileName = (name) => {
  const cleaned = (name || "").trim().replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || "Invoice";
};

// PDF-only currency formatter. jsPDF's built-in "helvetica" font only
// supports the WinAnsi character set, which has no glyph for "₹" (Indian
// Rupee, U+20B9). Passing that character to docPdf.text()/autoTable
// renders it as a stray fallback glyph and can corrupt the rest of the
// string on the same line — this is the root cause of the small "1"
// appearing before every amount, and of totals looking truncated. The
// on-screen `formatRupees` (which still shows "₹" in the React table/
// modals) is untouched; only PDF output uses this ASCII-safe "Rs."
// prefix, which every standard PDF font renders correctly.
const formatRupeesPdf = (value) => {
  const num = Number(value);
  if (!value || Number.isNaN(num)) return "Rs. 0";
  return `Rs. ${num.toLocaleString("en-IN")}`;
};

// ---------------------------------------------------------------------
// Shared header — used identically by BOTH PDFs. The logo and the
// company name are anchored to the same vertical center of the logo
// box (rather than two independently guessed Y offsets), so they can
// never appear vertically offset from one another again.
// ---------------------------------------------------------------------
const drawPdfHeader = (docPdf, { logoDataUrl, pageWidth, marginX, docTitle, metaLines }) => {
  const { colors } = PDF_THEME;
  const headerTop = 40;
  const logoSize = 54;

  if (logoDataUrl) {
    try {
      docPdf.addImage(logoDataUrl, "JPEG", marginX, headerTop, logoSize, logoSize);
    } catch (imgErr) {
      console.error("Failed to draw logo on PDF:", imgErr);
    }
  }

  const textX = marginX + logoSize + 16;
  const nameY = headerTop + logoSize / 2 - 6;
  const addressMaxWidth = pageWidth - marginX * 2 - logoSize - 16 - 200;

  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(17);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(COMPANY_NAME, textX, nameY);

  docPdf.setFont(PDF_THEME.fontFamily, "normal");
  docPdf.setFontSize(9);
  docPdf.setTextColor(...colors.textMuted);
  docPdf.text(COMPANY_ADDRESS, textX, nameY + 15, { maxWidth: addressMaxWidth });
  docPdf.text(`Phone: ${COMPANY_PHONE}`, textX, nameY + 27);
  docPdf.text(`Email: ${COMPANY_EMAIL}`, textX, nameY + 39);
  docPdf.text(`Website: ${COMPANY_WEBSITE}`, textX, nameY + 51);

  // Right-aligned document title + meta lines (invoice no./date, or
  // report generated-on date) — same block shape on both PDFs.
  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(20);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(docTitle, pageWidth - marginX, headerTop + 16, { align: "right" });

  docPdf.setFont(PDF_THEME.fontFamily, "normal");
  docPdf.setFontSize(9.5);
  docPdf.setTextColor(...colors.textMuted);
  metaLines.forEach((line, idx) => {
    docPdf.text(line, pageWidth - marginX, headerTop + 34 + idx * 14, { align: "right" });
  });

  const headerBottom =
    Math.max(headerTop + logoSize, nameY + 51, headerTop + 34 + metaLines.length * 14) + 16;

  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.3);
  docPdf.line(marginX, headerBottom, pageWidth - marginX, headerBottom);

  return headerBottom + 22;
};

// Consistent section heading with a short accent rule underneath — used
// for every section on both PDFs so visual hierarchy reads the same way
// throughout the document.
const drawSectionTitle = (docPdf, text, x, y) => {
  const { colors } = PDF_THEME;
  docPdf.setFont(PDF_THEME.fontFamily, "bold");
  docPdf.setFontSize(11.5);
  docPdf.setTextColor(...colors.primaryDark);
  docPdf.text(text.toUpperCase(), x, y);
  docPdf.setDrawColor(...colors.accentGold);
  docPdf.setLineWidth(1.1);
  docPdf.line(x, y + 5, x + 34, y + 5);
  return y + 22;
};

// Shared table look — grid borders, consistent padding/row height,
// dark-navy header, subtle row striping. Spread into every autoTable
// call on both PDFs so column spacing/typography never diverges.
const baseTableOptions = () => ({
  theme: "grid",
  styles: {
    font: PDF_THEME.fontFamily,
    fontSize: 9,
    cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    textColor: PDF_THEME.colors.textBody,
    lineColor: PDF_THEME.colors.borderLight,
    lineWidth: 0.6,
    valign: "middle",
  },
  headStyles: {
    fillColor: PDF_THEME.colors.primary,
    textColor: 255,
    fontStyle: "bold",
    fontSize: 9,
    halign: "left",
  },
  alternateRowStyles: { fillColor: PDF_THEME.colors.rowStripe },
});

// Computes the TRUE, live total of material/construction expense already
// consumed against a given invoice, straight from the
// `paymentAllocations/{invoiceId}` document data. This is the single
// formula Invoice.js uses everywhere balance is shown, so it can never
// drift from what Materials.js reports.
//
// - Entries in `materialEntries` tagged source === "materials" were
//   logged directly in Materials.js and are counted here.
// - Entries tagged source === "invoice" are just synced COPIES of rounds
//   already present in `allocations` — skipped here to avoid double
//   counting.
// - Every round in `allocations` is always counted directly, regardless
//   of whether Materials.js has synced it into materialEntries yet, so
//   the total is correct even if nobody has opened Materials.js since the
//   last "Log Construction Expense" save.
const getConsumedMaterialTotal = (allocData) => {
  if (!allocData) return 0;

  const materialsSourced = Array.isArray(allocData.materialEntries)
    ? allocData.materialEntries.filter((e) => e.source === "materials")
    : [];
  const directEntriesTotal = materialsSourced.reduce(
    (sum, e) => sum + (Number(e.totalCost) || 0),
    0,
  );

  const allocationRounds = Array.isArray(allocData.allocations) ? allocData.allocations : [];
  const allocationRoundsTotal = allocationRounds.reduce(
    (sum, r) => sum + (Number(r.amountAllocated) || 0),
    0,
  );

  return directEntriesTotal + allocationRoundsTotal;
};

// ---------------------------------------------------------------------
// Invoice numbering v2 — hierarchical Main + Child invoice numbers
//
//   mainInvoiceNumber     -> the permanent, professional, GLOBALLY unique
//                            number that belongs to the PROJECT itself,
//                            e.g. "SC-INV-2026-000001". Generated exactly
//                            ONCE per invoice document, from a shared
//                            Firestore counter (`counters/mainInvoiceNumber`)
//                            inside a transaction, so two projects — even
//                            created back-to-back — can never collide.
//                            Ideally it's created the moment the project is
//                            moved to Invoice (see Estimation1.js), but any
//                            legacy invoice that predates this upgrade gets
//                            one lazily backfilled here — exactly once,
//                            never regenerated on subsequent renders.
//
//   paymentInvoiceNumber  -> the permanent, per-PAYMENT child number,
//                            DERIVED deterministically from the project's
//                            mainInvoiceNumber + that payment's sequence:
//                                `${mainInvoiceNumber}-01`
//                                `${mainInvoiceNumber}-02`
//                                `${mainInvoiceNumber}-03`
//                            It is never generated from its own counter —
//                            its uniqueness is simply inherited from the
//                            already-unique mainInvoiceNumber, so there is
//                            no extra transaction cost and zero collision
//                            risk. It's computed once at payment-recording
//                            time and stored on the payment entry so it
//                            never has to be recomputed or can drift.
//
// Legacy documents saved before this upgrade only have a numeric
// `invoiceNumber` (1, 2, 3...) or an earlier professional string like
// "INV-2026-0001" on the payment entry, and no `mainInvoiceNumber` on the
// invoice doc at all. Every helper below falls back gracefully through
// each older format so old data keeps rendering and pairing correctly.
// ---------------------------------------------------------------------

const MAIN_INVOICE_PREFIX = "SC-INV";

// Formats the final sequence portion of a main invoice number as 3
// digits, left-padded with zeros (1 -> "001", 25 -> "025"), without ever
// truncating a value that already has more than 3 digits (1001 -> "1001").
const formatInvoiceSeq = (seq) => {
  const num = Number(seq);
  if (Number.isNaN(num)) return String(seq).padStart(3, "0");
  return String(num).padStart(3, "0");
};

// Pulls the trailing numeric sequence out of a client ID (e.g. "CL-003"
// -> "003", "CL-10082026-001" -> "001") so the main invoice number can
// visually align with the client's own ID. Returns null when the client
// ID has no usable trailing digits, so callers can fall back to the
// existing counter-based sequence instead of producing a broken number.
const extractClientNumericSeq = (clientId) => {
  if (!clientId || typeof clientId !== "string") return null;
  const match = clientId.trim().match(/(\d+)\s*$/);
  if (!match) return null;
  return formatInvoiceSeq(match[1]);
};

const buildMainInvoiceNumber = (year, clientSeq, projectSeq) =>
  `${MAIN_INVOICE_PREFIX}-${year}-${clientSeq}-P${projectSeq}`;

const buildPaymentInvoiceNumber = (mainInvoiceNumber, sequence) =>
  mainInvoiceNumber && sequence
    ? `${mainInvoiceNumber}-${String(sequence).padStart(2, "0")}`
    : null;

const getEntrySequence = (entry) => {
  if (!entry) return null;
  if (entry.invoiceSequence !== undefined && entry.invoiceSequence !== null) {
    return Number(entry.invoiceSequence);
  }
  if (typeof entry.invoiceNumber === "number") return entry.invoiceNumber;
  return null;
};

// Project-level (main) invoice number for the whole row/project. Shows a
// gentle pending label while the lazy one-time backfill is in flight.
const formatMainInvoiceNumber = (invoice, pendingLabel = "Generating...") => {
  if (!invoice) return "-";
  if (typeof invoice.mainInvoiceNumber === "string" && invoice.mainInvoiceNumber.trim()) {
    return invoice.mainInvoiceNumber;
  }
  return pendingLabel;
};

// Payment/child invoice number for a single payment entry. Prefers the new
// derived format (mainInvoiceNumber + sequence), then falls back through
// every earlier format this app has ever stored, so nothing on screen ever
// regresses for older data.
const formatInvoiceNumberDisplay = (entry, invoice) => {
  if (!entry) return "-";

  const seq = getEntrySequence(entry);

  // New: derive directly from the project's mainInvoiceNumber.
  if (invoice?.mainInvoiceNumber && seq) {
    return buildPaymentInvoiceNumber(invoice.mainInvoiceNumber, seq);
  }

  // Already computed & stored on the entry (covers the transaction that
  // just ran before fetchInvoices() has re-synced the invoice prop).
  if (typeof entry.paymentInvoiceNumber === "string" && entry.paymentInvoiceNumber.trim()) {
    return entry.paymentInvoiceNumber;
  }

  // Phase 1 upgrade's professional string (e.g. "INV-2026-0001").
  if (typeof entry.invoiceNumber === "string" && entry.invoiceNumber.trim()) {
    return entry.invoiceNumber;
  }

  // Original legacy numeric-only invoices never got a professional number.
  return seq ? `Legacy #${seq}` : "-";
};

function Invoice() {
  const navigate = useNavigate();

  const { toasts, toast } = useToast();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Single source of truth for status, shared with Clients/Estimation/
  // Materials via a realtime Firestore subscription.
  const { clientsById } = useClientsStatusMap();

  // Payment-allocation rounds for every invoice, keyed by invoice.id (the
  // paymentAllocations doc id === invoices doc id). Loaded once alongside
  // invoices so every row/PDF/view-modal can render the Construction
  // Expense breakdown without extra clicks. This map is READ-ONLY here for
  // display purposes — Invoice.js never derives payment eligibility from
  // it, and never consumes/subtracts from it. Materials.js owns all
  // expense-vs-budget calculation logic.
  const [allocationsMap, setAllocationsMap] = useState({});

  // Tracks invoice ids currently being backfilled with a mainInvoiceNumber
  // so the lazy one-time generation effect below never fires twice for the
  // same doc (e.g. across re-renders while the transaction is in flight).
  const backfillingRef = useRef(new Set());

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({
    clientName: "",
    email: "",
    address: "",
    projectType: "",
    notes: "",
    finalEstimation: "",
  });
  const [editErrors, setEditErrors] = useState({ email: "", finalEstimation: "" });
  const [saving, setSaving] = useState(false);

  // ---------------- Project completion workflow ----------------
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingInvoice, setCompletingInvoice] = useState(null);
  const [completingSaving, setCompletingSaving] = useState(false);

  // ---------------- Payment history workflow ----------------
  // Every payment appends a new, immutable entry to the invoice doc's
  // `payments` array (never overwritten) — this array IS the invoice
  // history: entry.invoiceSequence is 1, 2, 3... and entry's derived
  // paymentInvoiceNumber ("SC-INV-2026-000021-01", "-02", "-03"...) is the
  // permanent professional identifier shown throughout the UI. The invoice
  // doc itself keeps rolling totals (amountPaid, balanceAmount,
  // paymentCount, latestPaymentDate) so the table can render instantly
  // without re-summing history on every load.
  //
  // Each payment is an independent bucket — Payment 1, Payment 2, Payment 3
  // etc. are never merged. Recording a new payment has NO dependency on
  // whatever has or hasn't been logged in Construction Expense Allocation
  // for earlier payments; see isPayable() below.
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);

  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [historyInvoice, setHistoryInvoice] = useState(null);

  // Inline "edit history" state for the Payment History modal (Section 6 —
  // Payment Date & Payment Notes are editable; the amount, invoice number
  // and every balance figure never are).
  const [editingHistorySeq, setEditingHistorySeq] = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({ paymentDate: "", paymentNotes: "" });
  const [historyEditSaving, setHistoryEditSaving] = useState(false);

   // Per-payment PDF export — keyed by that payment's sequence number, so
  // generating one payment's PDF only disables that specific card's
  // button, not every card in the modal.
  const [paymentEntryPdfGeneratingSeq, setPaymentEntryPdfGeneratingSeq] = useState(null);

  // ---------------- Construction Expense Allocation workflow ----------------
  // Lives in its own `paymentAllocations/{invoiceId}` document so it never
  // duplicates or overwrites the Estimation Budget Plan. Every save appends
  // a new history entry via arrayUnion — previous entries are never
  // touched. This is a pure record-keeping log of how money already paid
  // has been spent on day-to-day construction expenses; it does not gate,
  // block, or otherwise depend on recording the client's next payment.
  //
  // NOTE: Logging an expense here is NEVER blocked, even if it pushes the
  // client's available payment balance negative. There is no more
  // "Insufficient / Blocked" check of any kind — the admin can always
  // record a construction expense, and the Available/Remaining chips
  // below simply reflect a negative number (highlighted in red) when
  // expenses have outpaced what the client has paid so far. The same
  // entry is picked up by Materials.js's Daily Material Logs, which shows
  // the identical negative-allowed Remaining Payment indicator.
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocatingInvoice, setAllocatingInvoice] = useState(null);
  const [allocationRows, setAllocationRows] = useState([{ ...EMPTY_ALLOCATION_ROW, id: "row-0" }]);
  const [liveAmountPaid, setLiveAmountPaid] = useState(0);
  const [consumedMaterialTotal, setConsumedMaterialTotal] = useState(0);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [allocationSaving, setAllocationSaving] = useState(false);
  const [allocationError, setAllocationError] = useState("");

  const [showViewAllocationModal, setShowViewAllocationModal] = useState(false);
  const [viewingAllocationInvoice, setViewingAllocationInvoice] = useState(null);
  const [viewAllocations, setViewAllocations] = useState([]);
  const [viewAllocationLoading, setViewAllocationLoading] = useState(false);
  const [viewLiveAmountPaid, setViewLiveAmountPaid] = useState(0);
  const [viewConsumedTotal, setViewConsumedTotal] = useState(0);
  // ---------------- Manage Project (was: Delete confirmation) ----------------
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [onHoldSaving, setOnHoldSaving] = useState(false);
  const [backToEstimationSaving, setBackToEstimationSaving] = useState(false);
  const [rejectSaving, setRejectSaving] = useState(false);
  const manageActionBusy = onHoldSaving || backToEstimationSaving || rejectSaving;

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "invoices"));
      const list = [];
      snapshot.forEach((docSnap) => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });

      list.sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      });

      setInvoices(list);
    } catch (err) {
      console.error("Failed to load invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  // Loaded once alongside invoices — read-only here. Nothing on this page
  // ever creates or edits a paymentAllocations doc directly except the
  // existing "Construction Expense Allocation" flow further down, which
  // already owned this collection before this upgrade.
  const fetchAllocationsMap = async () => {
    try {
      const snapshot = await getDocs(collection(db, "paymentAllocations"));
      const map = {};
      snapshot.forEach((docSnap) => {
        map[docSnap.id] = docSnap.data().allocations || [];
      });
      setAllocationsMap(map);
    } catch (err) {
      console.error("Failed to load payment allocations:", err);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchAllocationsMap();
  }, []);

  // ---------------------------------------------------------------------
  // Lazy, one-time backfill of `mainInvoiceNumber` for legacy invoices.
  //
  // New invoices get their mainInvoiceNumber the moment they're created
  // (see Estimation1.js's finalizeMoveToInvoice), so in the normal case
  // this effect finds nothing to do. For any invoice that predates that
  // change, it generates exactly one permanent number — inside a Firestore
  // transaction against `counters/mainInvoiceNumber` — and merges it back
  // into local state. backfillingRef guards against ever re-processing the
  // same doc twice, even across fast re-renders.
  // ---------------------------------------------------------------------
  const ensureMainInvoiceNumber = async (invoice) => {
    if (invoice.mainInvoiceNumber) return invoice.mainInvoiceNumber;

    const invoiceRef = doc(db, "invoices", invoice.id);
    const counterRef = doc(db, "counters", "mainInvoiceNumber");
    const clientRef = invoice.clientDocId ? doc(db, "clients", invoice.clientDocId) : null;
    const year = invoice.createdAt?.toDate
      ? invoice.createdAt.toDate().getFullYear()
      : new Date().getFullYear();

    const fallbackProjectNumber = invoice.clientDocId
      ? resolveProjectNumber(clientsById, invoice.clientDocId)
      : 1;

    return runTransaction(db, async (transaction) => {
      const snap = await transaction.get(invoiceRef);
      if (!snap.exists()) return null;

      const fresh = snap.data();
      if (fresh.mainInvoiceNumber) return fresh.mainInvoiceNumber;

      const clientSnap = clientRef ? await transaction.get(clientRef) : null;
      const storedProjectNumber = clientSnap?.exists() ? clientSnap.data().projectNumber : null;
      const projectSeq = formatProjectSeq(storedProjectNumber || fallbackProjectNumber);

      const clientSeq = extractClientNumericSeq(fresh.clientId || invoice.clientId);
      let mainInvoiceNumber;

      if (clientSeq !== null) {
        mainInvoiceNumber = buildMainInvoiceNumber(year, clientSeq, projectSeq);
        transaction.update(invoiceRef, { mainInvoiceNumber });
      } else {
        const counterSnap = await transaction.get(counterRef);
        const lastSeq = counterSnap.exists() ? Number(counterSnap.data().lastSequence) || 0 : 0;
        const nextSeq = lastSeq + 1;
        mainInvoiceNumber = buildMainInvoiceNumber(year, formatInvoiceSeq(nextSeq), projectSeq);

        transaction.update(invoiceRef, { mainInvoiceNumber });
        transaction.set(
          counterRef,
          { lastSequence: nextSeq, updatedAt: new Date().toISOString() },
          { merge: true },
        );
      }

      return mainInvoiceNumber;
    });
  };
  useEffect(() => {
    if (loading) return;

    const missing = invoices.filter(
      (inv) => !inv.mainInvoiceNumber && !backfillingRef.current.has(inv.id),
    );
    if (missing.length === 0) return;

    missing.forEach((inv) => backfillingRef.current.add(inv.id));

    (async () => {
      const results = await Promise.all(
        missing.map(async (inv) => {
          try {
            const mainInvoiceNumber = await ensureMainInvoiceNumber(inv);
            return mainInvoiceNumber ? { id: inv.id, mainInvoiceNumber } : null;
          } catch (err) {
            console.error("Failed to backfill main invoice number:", inv.id, err);
            return null;
          } finally {
            backfillingRef.current.delete(inv.id);
          }
        }),
      );

      const valid = results.filter(Boolean);
      if (valid.length === 0) return;

      setInvoices((prev) =>
        prev.map((inv) => {
          const found = valid.find((v) => v.id === inv.id);
          return found ? { ...inv, mainInvoiceNumber: found.mainInvoiceNumber } : inv;
        }),
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoices]);

  const formatRupees = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return "₹ 0";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  // Same idea as Materials.js's formatSignedRupees — used for the
  // Construction Expense Allocation chips below, which are now allowed to
  // show a negative value ("expenses exceed what's been paid so far")
  // instead of being clamped to zero and blocked.
  const formatSignedRupees = (value) => {
    const num = Number(value) || 0;
    const abs = Math.abs(num);
    const formatted = `₹ ${abs.toLocaleString("en-IN")}`;
    return num < 0 ? `-${formatted}` : formatted;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // ---------------------------------------------------------------------
  // Corrected, always-current balance-after-payment for a payment entry.
  //
  // `entry.balanceAfterPayment` is a SNAPSHOT taken at the moment that
  // payment was recorded. If the Final Quotation is edited afterward
  // (Payments.js -> Edit Final Quotation), that stored snapshot goes
  // stale — it keeps reflecting the OLD Final Quotation forever, even
  // though `invoice.balanceAmount` (and Financial Summary) already gets
  // updated correctly. This recomputes the balance live against the
  // invoice's CURRENT finalEstimation, so Financial Summary, Invoice
  // Timeline, Payment History, and every generated PDF always agree and
  // never use the original/stale estimation amount.
  // ---------------------------------------------------------------------
  const getCorrectedBalanceAfterPayment = (invoice, runningTotalPaid) => {
    const currentFinal = Number(invoice?.finalEstimation) || 0;
    return currentFinal - runningTotalPaid;
  };

  // Status to display for a given invoice row — always resolved from the
  // live clients map (single source of truth), never from a locally-stored
  // copy on the invoice document.
const statusFor = useCallback(
    (invoice) => resolveClientStatus(clientsById, invoice.clientDocId, "In Progress"),
    [clientsById],
  );

  // Maps live status to the pill's CSS class. "On Hold" gets its own
  // color instead of falling into the generic "In Progress" blue.
  const statusPillClass = (status) => {
    if (status === "Completed") return "status-completed";
    if (status === "On Hold") return "status-onhold";
    return "status-progress";
  };

// A project only "lives" on the Invoice page while its status is
  // In Progress / On Hold / Completed. "In Talk" (moved back to
  // Estimation) and "Rejected" (deleted permanently) are hidden here, but
  // their client/estimation/invoice documents are never touched — they
  // just stop surfacing on this page.
  const visibleInvoices = useMemo(
    () =>
      invoices.filter((inv) => {
        const status = statusFor(inv);
        return status !== "In Talk" && status !== "Rejected";
      }),
    [invoices, statusFor],
  );

  const filteredInvoices = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return visibleInvoices;

    return visibleInvoices.filter((inv) => {
      const clientId = (inv.clientId || "").toLowerCase();
      const name = (inv.clientName || "").toLowerCase();
      const email = (inv.email || "").toLowerCase();
      const mainNumber = (inv.mainInvoiceNumber || "").toLowerCase();

      return (
        clientId.includes(term) ||
        name.includes(term) ||
        email.includes(term) ||
        mainNumber.includes(term)
      );
    });
  }, [search, visibleInvoices]);

  const {
    pageData: paginatedInvoices,
    ...invoicesPagination
  } = usePagination(filteredInvoices, { resetKey: search });

  const totalBilled = useMemo(
    () => visibleInvoices.reduce((sum, inv) => sum + (Number(inv.finalEstimation) || 0), 0),
    [visibleInvoices],
  );

  const totalOutstanding = useMemo(
    () => visibleInvoices.reduce((sum, inv) => sum + (Number(inv.balanceAmount) || 0), 0),
    [visibleInvoices],
  );

  // ---------------------------------------------------------------------
  // Payment helpers
  //
  // NOTE: The previous "sequential payment" restriction has been removed.
  // Payments used to be blocked until the prior payment's Construction
  // Expense Allocation round was saved; that gate no longer exists. A
  // client's payment can be recorded the moment it is received — ₹5,00,000
  // today, ₹10,00,000 next week, ₹3,00,000 after that — with no dependency
  // on whatever has or hasn't been logged in Construction Expense
  // Allocation. Invoice.js's only job around payments is to record them,
  // keep the history, and maintain the sequence; all expense/material
  // consumption logic lives entirely in Materials.js.
  // ---------------------------------------------------------------------

  const sortedPaymentsFor = (invoice) =>
    [...(invoice.payments || [])].sort(
      (a, b) => (getEntrySequence(a) || 0) - (getEntrySequence(b) || 0),
    );

  // A project remains payable for as long as it has an outstanding
  // balance — independent of any Construction Expense Allocation state.
  const isPayable = (invoice) => Number(invoice.balanceAmount) > 0;

  // ---------------- View ----------------
  const openView = (invoice) => {
    setViewingInvoice(invoice);
    setShowViewModal(true);
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewingInvoice(null);
  };

  const goToMaterialsFor = (invoice) => {
    const params = new URLSearchParams({
      invoiceId: invoice.id,
      clientDocId: invoice.clientDocId || "",
      clientId: invoice.clientId || "",
      clientName: invoice.clientName || "",
      projectType: invoice.projectType || "",
    });
    navigate(`/dashboard/materials?${params.toString()}`);
  };

  // ---------------- Edit ----------------
  // Status is no longer editable here — project completion is handled by
  // the dedicated "Mark as Completed" workflow below, which enforces the
  // ₹0-balance rule. Client/project details plus free-text project Notes
  // can still be corrected in place. Invoice numbers, historical payment
  // amounts and balances are never editable anywhere in this component.
  const openEdit = (invoice) => {
    setEditForm({
      clientName: invoice.clientName || "",
      email: invoice.email || "",
      address: invoice.address || "",
      projectType: invoice.projectType || "",
      notes: invoice.notes || "",
      finalEstimation: String(invoice.finalEstimation ?? ""),
    });
    setEditErrors({ email: "", finalEstimation: "" });
    setEditingInvoice(invoice);
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditingInvoice(null);
    setEditErrors({ email: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFinalEstimationChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setEditForm((prev) => ({ ...prev, finalEstimation: value }));
      if (editErrors.finalEstimation) {
        setEditErrors((prev) => ({ ...prev, finalEstimation: "" }));
      }
    }
  };

  const validateEditForm = () => {
    const errors = { email: "", finalEstimation: "" };
    let isValid = true;

    if (!EMAIL_REGEX.test(editForm.email.trim())) {
      errors.email = "Enter a valid email address (e.g. name@example.com).";
      isValid = false;
    }

    const newFinal = Number(editForm.finalEstimation);
    const amountPaid = Number(editingInvoice?.amountPaid) || 0;

    if (!editForm.finalEstimation.trim() || Number.isNaN(newFinal) || newFinal <= 0) {
      errors.finalEstimation = "Enter a valid Final Quotation amount.";
      isValid = false;
    } else if (newFinal < amountPaid) {
      errors.finalEstimation = `Final Quotation cannot be less than the amount already paid (${formatRupees(amountPaid)}).`;
      isValid = false;
    }

    setEditErrors(errors);
    return isValid;
  };

  const saveEdit = async () => {
    if (!editingInvoice) return;

    if (!validateEditForm()) return;

    setSaving(true);

    try {
      const newFinal = Number(editForm.finalEstimation);
      const amountPaid = Number(editingInvoice.amountPaid) || 0;
      const quotationChanged = newFinal !== Number(editingInvoice.finalEstimation);
      const newBalance = newFinal - amountPaid;

      // amountPaid and the payments history are intentionally NOT touched
      // here — they are derived exclusively from "Add Payment". Only
      // finalEstimation + balanceAmount are recalculated when the Final
      // Quotation itself changes, exactly like Payments.js's saveQuotation.
      await updateDoc(doc(db, "invoices", editingInvoice.id), {
        clientName: editForm.clientName,
        email: editForm.email,
        address: editForm.address,
        projectType: editForm.projectType,
        notes: editForm.notes,
        finalEstimation: newFinal,
        balanceAmount: newBalance,
      });

      // Keep the linked Estimation record in sync so the Estimation page
      // never shows a stale amount after the Final Quotation is edited
      // here — same sync Payments.js's saveQuotation already performs.
      if (quotationChanged && editingInvoice.estimationId) {
        await updateDoc(doc(db, "estimations", editingInvoice.estimationId), {
          estimationAmount: newFinal,
        });
      }

      toast.success("Changes Saved Successfully");

      closeEdit();
      await fetchInvoices();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update invoice.");
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Delete ----------------
// ---------------- Manage Project (was: Delete) ----------------
  const requestDeleteInvoice = (invoice) => {
    setDeleteTarget(invoice);
  };

  const cancelDeleteInvoice = () => {
    if (manageActionBusy) return;
    setDeleteTarget(null);
  };

  // Move to On Hold — status-only change. The invoice doc, its payments
  // array, and every balance figure are untouched, so nothing is lost.
  const handleMoveToOnHold = async () => {
    if (!deleteTarget?.clientDocId) {
      toast.error("This invoice has no linked client record.");
      return;
    }
    setOnHoldSaving(true);
    try {
      await updateDoc(doc(db, "clients", deleteTarget.clientDocId), {
        status: "On Hold",
      });
      toast.success("Project Moved to On Hold");
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to move project to On Hold.");
    } finally {
      setOnHoldSaving(false);
    }
  };

  // Move Back to Estimation — reverses the estimation's `invoiced` flag,
  // resets client status to "In Talk", and removes the mistaken invoice
  // doc so re-running "Move to Invoice" later never creates a duplicate.
  // Blocked if real payments already exist, so payment history can never
  // be silently discarded.
  const handleMoveBackToEstimation = async () => {
    if (!deleteTarget?.clientDocId || !deleteTarget?.estimationId) {
      toast.error("Missing linked client or estimation record.");
      return;
    }

    if (Number(deleteTarget.amountPaid) > 0) {
      toast.error(
        "This project already has recorded payments and cannot be moved back to Estimation.",
      );
      return;
    }

    setBackToEstimationSaving(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "clients", deleteTarget.clientDocId), {
        status: "In Talk",
      });
      batch.update(doc(db, "estimations", deleteTarget.estimationId), {
        invoiced: false,
      });
      batch.delete(doc(db, "invoices", deleteTarget.id));
      await batch.commit();

      toast.success("Project Moved Back to Estimation");
      setDeleteTarget(null);
      await fetchInvoices();
    } catch (err) {
      console.error(err);
      toast.error("Failed to move project back to Estimation.");
    } finally {
      setBackToEstimationSaving(false);
    }
  };

  // Delete Permanently — reuses the exact rejection pattern already used
  // in Clients.js/Estimation1.js (plain client status update). No
  // Firestore document is ever deleted, so invoice/payment history stays.
  const handleRejectPermanently = async () => {
    if (!deleteTarget?.clientDocId) {
      toast.error("This invoice has no linked client record.");
      return;
    }
    setRejectSaving(true);
    try {
      await updateDoc(doc(db, "clients", deleteTarget.clientDocId), {
        status: "Rejected",
      });
      toast.success("Client Moved to Rejected");
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to reject project.");
    } finally {
      setRejectSaving(false);
    }
  };

  // ---------------- Project completion workflow ----------------
  const isCompletable = (invoice) =>
    Number(invoice.balanceAmount) === 0 && statusFor(invoice) !== "Completed";

  const openCompleteModal = (invoice) => {
    if (!isCompletable(invoice)) return;
    setCompletingInvoice(invoice);
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    if (completingSaving) return;
    setShowCompleteModal(false);
    setCompletingInvoice(null);
  };

  const confirmMarkCompleted = async () => {
    if (!completingInvoice) return;

    setCompletingSaving(true);

    try {
      // The client record is the single source of truth — updating it here
      // is what makes Clients, Estimation, and Materials all reflect
      // "Completed" immediately.
      if (completingInvoice.clientDocId) {
        await updateDoc(doc(db, "clients", completingInvoice.clientDocId), {
          status: "Completed",
        });
      }

      // Keep the originating estimation record's project-type/history tidy
      // too, though display everywhere reads from the client record.
      if (completingInvoice.estimationId) {
        await updateDoc(doc(db, "estimations", completingInvoice.estimationId), {
          completedAt: new Date().toISOString(),
        });
      }

      toast.success("Project marked as Completed.");

      closeCompleteModal();
      await fetchInvoices();
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark project as completed.");
    } finally {
      setCompletingSaving(false);
    }
  };

  // =====================================================================
  // Add Payment — generates the next child invoice under this project's
  // permanent Main Invoice Number: "{mainInvoiceNumber}-01", "-02", "-03"...
  //
  // Available whenever the invoice has an outstanding balance — there is
  // no dependency on Construction Expense Allocation having been logged
  // for any earlier payment. Each payment is stored as its own
  // independent bucket in the `payments` array and is never merged with
  // another.
  //
  // If this project somehow still doesn't have a mainInvoiceNumber (a
  // legacy invoice the backfill effect hasn't reached yet), it is
  // generated right here, inside the SAME Firestore transaction as the
  // payment itself, so there is never a separate write and never a chance
  // of two payments generating two different main numbers for one project.
  // =====================================================================

  const openAddPayment = (invoice) => {
    if (!isPayable(invoice)) return;
    setPayingInvoice(invoice);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentError("");
    setShowAddPaymentModal(true);
  };

  const closeAddPayment = () => {
    if (paymentSaving) return;
    setShowAddPaymentModal(false);
    setPayingInvoice(null);
    setPaymentAmount("");
    setPaymentDate("");
    setPaymentError("");
  };

  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setPaymentAmount(value);
      if (paymentError) setPaymentError("");
    }
  };

  const paymentPreviewBalance = useMemo(() => {
    if (!payingInvoice) return 0;
    const currentBalance = Number(payingInvoice.balanceAmount) || 0;
    const amt = Number(paymentAmount) || 0;
    const preview = currentBalance - amt;
    return preview > 0 ? preview : 0;
  }, [payingInvoice, paymentAmount]);

  const saveAddPayment = async () => {
    if (!payingInvoice) return;

    const amt = Number(paymentAmount);

    if (!paymentAmount.trim() || Number.isNaN(amt) || amt <= 0) {
      setPaymentError("Enter a valid payment amount.");
      return;
    }

    if (!paymentDate) {
      setPaymentError("Select a payment date.");
      return;
    }

    const currentBalance = Number(payingInvoice.balanceAmount) || 0;

    if (amt > currentBalance) {
      setPaymentError("Payment amount cannot exceed the current balance.");
      return;
    }

    setPaymentSaving(true);
    const invoiceRef = doc(db, "invoices", payingInvoice.id);
    const mainCounterRef = doc(db, "counters", "mainInvoiceNumber");
    const clientRef = payingInvoice.clientDocId ? doc(db, "clients", payingInvoice.clientDocId) : null;
    const fallbackProjectNumber = payingInvoice.clientDocId
      ? resolveProjectNumber(clientsById, payingInvoice.clientDocId)
      : 1;

    try {
      await runTransaction(db, async (transaction) => {
        // All reads before any writes, per Firestore transaction rules.
        const snap = await transaction.get(invoiceRef);

        if (!snap.exists()) {
          throw new Error("This invoice no longer exists.");
        }

        const data = snap.data();
        const priorBalance = Number(data.balanceAmount) || 0;
        const priorPaid = Number(data.amountPaid) || 0;
        const priorPayments = Array.isArray(data.payments) ? data.payments : [];

        if (amt > priorBalance) {
          throw new Error("Payment amount cannot exceed the current balance.");
        }

        // Ensure this project's permanent Main Invoice Number exists.
        // Every child/payment number is derived from it, so it must be in
        // place before the very first payment. Generated lazily, exactly
        // once, right here if a legacy invoice never got one — preferring
        // the client's own numeric ID sequence, and falling back to the
        // shared counter only when the client ID has no usable sequence.
        let mainInvoiceNumber = data.mainInvoiceNumber;
        let newCounterSeq = null;
        let generatedNewMainNumber = false;

        if (!mainInvoiceNumber) {
          const clientSnap = clientRef ? await transaction.get(clientRef) : null;
          const storedProjectNumber = clientSnap?.exists() ? clientSnap.data().projectNumber : null;
          const projectSeq = formatProjectSeq(storedProjectNumber || fallbackProjectNumber);

          const clientSeq = extractClientNumericSeq(data.clientId || payingInvoice.clientId);

          if (clientSeq !== null) {
            mainInvoiceNumber = buildMainInvoiceNumber(new Date().getFullYear(), clientSeq, projectSeq);
          } else {
            const counterSnap = await transaction.get(mainCounterRef);
            const lastSeq = counterSnap.exists()
              ? Number(counterSnap.data().lastSequence) || 0
              : 0;
            newCounterSeq = lastSeq + 1;
            mainInvoiceNumber = buildMainInvoiceNumber(
              new Date().getFullYear(),
              formatInvoiceSeq(newCounterSeq),
              projectSeq,
            );
          }
          generatedNewMainNumber = true;
        }

        const newBalance = priorBalance - amt;
        const nextSequence = priorPayments.length + 1;
        const paymentInvoiceNumber = buildPaymentInvoiceNumber(mainInvoiceNumber, nextSequence);

        // A brand-new, immutable invoice record — its own independent
        // payment bucket. Earlier entries in the array are never modified
        // or removed, and this write never checks or depends on whether
        // any prior payment has a Construction Expense Allocation round.
        const newInvoiceEntry = {
          invoiceSequence: nextSequence,
          paymentInvoiceNumber,
          paymentAmount: amt,
          paymentDate,
          paymentNotes: "",
          balanceAfterPayment: newBalance,
          createdAt: new Date().toISOString(),
        };

        transaction.update(invoiceRef, {
          ...(generatedNewMainNumber ? { mainInvoiceNumber } : {}),
          amountPaid: priorPaid + amt,
          balanceAmount: newBalance,
          payments: [...priorPayments, newInvoiceEntry],
          paymentCount: nextSequence,
          latestPaymentDate: paymentDate,
          latestInvoiceNumber: paymentInvoiceNumber,
        });

        if (newCounterSeq) {
          transaction.set(
            mainCounterRef,
            { lastSequence: newCounterSeq, updatedAt: new Date().toISOString() },
            { merge: true },
          );
        }
      });

      toast.success("Payment Recorded — Invoice Generated Successfully");
      closeAddPayment();
      await fetchInvoices();
    } catch (err) {
      console.error(err);
      const message = err.message || "Failed to record payment.";
      setPaymentError(message);
      toast.error(message);
    } finally {
      setPaymentSaving(false);
    }
  };

  // ---------------- Payment History (read-only totals, editable notes) ----------------
  const openPaymentHistory = (invoice) => {
    setHistoryInvoice(invoice);
    setEditingHistorySeq(null);
    setShowPaymentHistoryModal(true);
  };

  const closePaymentHistory = () => {
    if (historyEditSaving) return;
    setShowPaymentHistoryModal(false);
    setHistoryInvoice(null);
    setEditingHistorySeq(null);
  };

  const historyPayments = useMemo(() => {
    if (!historyInvoice) return [];
    return sortedPaymentsFor(historyInvoice);
  }, [historyInvoice]);

  // Each card shows a running total (sum of every payment up to and
  // including itself), and a CORRECTED remaining balance recomputed live
  // against the invoice's current Final Quotation (see
  // getCorrectedBalanceAfterPayment) rather than each entry's stale
  // stored `balanceAfterPayment` snapshot.
  const historyPaymentsWithRunning = useMemo(() => {
    if (!historyInvoice) return [];
    let running = 0;
    return historyPayments.map((entry) => {
      running += Number(entry.paymentAmount) || 0;
      return {
        entry,
        runningTotal: running,
        correctedBalance: getCorrectedBalanceAfterPayment(historyInvoice, running),
      };
    });
  }, [historyPayments, historyInvoice]);

  // Section 6 — Editable Invoice History: Payment Date & Payment Notes can
  // be corrected after the fact. Invoice Number, Payment Amount, and every
  // balance figure remain permanently locked.
  const openEditHistoryEntry = (entry) => {
    setEditingHistorySeq(getEntrySequence(entry));
    setHistoryEditForm({
      paymentDate: entry.paymentDate || "",
      paymentNotes: entry.paymentNotes || "",
    });
  };

  const cancelEditHistoryEntry = () => {
    if (historyEditSaving) return;
    setEditingHistorySeq(null);
  };

  const saveEditHistoryEntry = async () => {
    if (!historyInvoice || editingHistorySeq === null) return;

    if (!historyEditForm.paymentDate) {
      toast.error("Select a payment date.");
      return;
    }

    setHistoryEditSaving(true);
    const invoiceRef = doc(db, "invoices", historyInvoice.id);

    try {
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(invoiceRef);
        if (!snap.exists()) throw new Error("This invoice no longer exists.");

        const data = snap.data();
        const priorPayments = Array.isArray(data.payments) ? data.payments : [];

        const updatedPayments = priorPayments.map((entry) => {
          if (getEntrySequence(entry) !== editingHistorySeq) return entry;
          return {
            ...entry,
            paymentDate: historyEditForm.paymentDate,
            paymentNotes: historyEditForm.paymentNotes || "",
          };
        });

        // Keep the rolling "latest payment date" field honest if the most
        // recent entry is the one being edited.
        const latest = [...updatedPayments].sort(
          (a, b) => (getEntrySequence(a) || 0) - (getEntrySequence(b) || 0),
        )[updatedPayments.length - 1];

        transaction.update(invoiceRef, {
          payments: updatedPayments,
          latestPaymentDate: latest ? latest.paymentDate : data.latestPaymentDate || null,
        });
      });

      toast.success("Payment Details Updated Successfully");
      setEditingHistorySeq(null);
      await fetchInvoices();

      // Keep the open modal's data current without forcing the user to
      // reopen it.
      setHistoryInvoice((prev) => {
        if (!prev) return prev;
        const updatedPayments = (prev.payments || []).map((entry) =>
          getEntrySequence(entry) === editingHistorySeq
            ? { ...entry, paymentDate: historyEditForm.paymentDate, paymentNotes: historyEditForm.paymentNotes }
            : entry,
        );
        return { ...prev, payments: updatedPayments };
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to update payment details.");
    } finally {
      setHistoryEditSaving(false);
    }
  };

  // =====================================================================
  // Single Payment PDF — generated from the Payment History modal's
  // per-card "Generate PDF" button, AND from the Invoice Timeline's
  // "Generate PDF" button in the View Invoice modal. Reuses the exact
  // same PDF_THEME, drawPdfHeader/drawSectionTitle/baseTableOptions
  // helpers as every other PDF in this file, but contains ONLY the one
  // selected payment entry — never the client's other payments.
  //
  // `invoiceForPdf` is passed explicitly (rather than always reading
  // `historyInvoice`) so this same function can be called from either the
  // Payment History modal (historyInvoice) or the View Invoice modal's
  // Invoice Timeline (viewingInvoice) without duplicating this logic.
  //
  // `correctedBalance`, when provided, is used in place of the entry's
  // stale stored `balanceAfterPayment` snapshot — see
  // getCorrectedBalanceAfterPayment above.
  // =====================================================================
  const generatePaymentEntryPDF = async (invoiceForPdf, entry, runningTotal, correctedBalance) => {
    if (!invoiceForPdf || !entry) return;

    const seq = getEntrySequence(entry);
    setPaymentEntryPdfGeneratingSeq(seq);
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      const logoDataUrl = await loadCompanyLogoDataUrl();
      const paymentInvoiceNumber = formatInvoiceNumberDisplay(entry, invoiceForPdf);
      const paymentDateLabel = formatDate(entry.paymentDate);
      // Use the corrected, current-Final-Quotation balance rather than the
      // stale snapshot stored on the entry.
      const balanceForPdf =
        correctedBalance !== undefined ? correctedBalance : entry.balanceAfterPayment;

      let cursorY = drawPdfHeader(docPdf, {
        logoDataUrl,
        pageWidth,
        marginX,
        docTitle: "PAYMENT RECEIPT",
        metaLines: [
          `Payment Invoice Number: ${paymentInvoiceNumber}`,
          `Payment Date: ${paymentDateLabel}`,
          `Main Invoice Number: ${formatMainInvoiceNumber(invoiceForPdf)}`,
        ],
      });

      // ---- Client & Project Details ----
      cursorY = drawSectionTitle(docPdf, "Client & Project Details", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        theme: "grid",
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        showHead: false,
        body: [
          ["Client Name", invoiceForPdf.clientName || "-", "Project Type", invoiceForPdf.projectType || "-"],
          ["Client ID", invoiceForPdf.clientId || "-", "Status", statusFor(invoiceForPdf)],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          1: { cellWidth: 195 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Payment Details — this specific payment only ----
      cursorY = drawSectionTitle(docPdf, "Payment Details", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        theme: "grid",
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        showHead: false,
        body: [
          [
            "Final Quotation",
            formatRupeesPdf(invoiceForPdf.finalEstimation),
            "Total Amount Paid",
            formatRupeesPdf(invoiceForPdf.amountPaid),
          ],
          ["Payment Invoice Number", paymentInvoiceNumber, "Payment Date", paymentDateLabel],
          [
            "Payment Amount",
            { content: formatRupeesPdf(entry.paymentAmount), styles: { textColor: colors.paidGreen, fontStyle: "bold" } },
            "Balance Remaining",
            { content: formatRupeesPdf(balanceForPdf), styles: { textColor: colors.balanceRed, fontStyle: "bold" } },
          ],
          ["Payment Notes", entry.paymentNotes || "-", "", ""],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 130 },
          1: { cellWidth: 155 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 110 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX },
      });

      cursorY = docPdf.lastAutoTable.finalY + 30;

      // ---- Totals ----
      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, cursorY, pageWidth - marginX, cursorY);
      cursorY += 20;

      autoTable(docPdf, {
        startY: cursorY,
        theme: "plain",
        styles: {
          font: PDF_THEME.fontFamily,
          fontSize: 10.5,
          cellPadding: { top: 6, bottom: 6, left: 0, right: 0 },
          fontStyle: "bold",
          textColor: colors.primaryDark,
        },
        body: [
          ["Final Quotation", formatRupeesPdf(invoiceForPdf.finalEstimation)],
          ["This Payment", formatRupeesPdf(entry.paymentAmount)],
          ["Balance Remaining", formatRupeesPdf(balanceForPdf)],
        ],
        columnStyles: { 1: { halign: "right" } },
        margin: { left: marginX, right: marginX },
      });

      // ---- Footer (every page) ----
      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This receipt is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, { align: "right" });
      }

      const fileSuffix = paymentInvoiceNumber !== "-" ? paymentInvoiceNumber : `Payment_${seq ?? ""}`;
      docPdf.save(`${sanitizeFileName(invoiceForPdf.clientName)}_${sanitizeFileName(fileSuffix)}.pdf`);
    } catch (err) {
      console.error("Failed to generate payment PDF:", err);
      toast.error("Failed to generate payment PDF.");
    } finally {
      setPaymentEntryPdfGeneratingSeq(null);
    }
  };

  // =====================================================================
  // Construction Expense Allocation (formerly labelled "Payment
  // Allocation" / "Allocate Current Payment").
  //
  // This is deliberately separate from Estimation's Budget Plan. Budget
  // Planning says "how the whole project's money SHOULD be spent"; this
  // says "how money already paid has actually been spent on day-to-day
  // construction expenses". Stored in its own
  // `paymentAllocations/{invoiceId}` doc, keyed by clientDocId + invoiceId,
  // with an ever-growing `allocations` history array (never overwritten).
  //
  // IMPORTANT: This is purely a record-keeping log. It is NOT a gate on
  // recording the client's next payment — that sequential dependency has
  // been removed entirely (see isPayable above). It is also NOT a gate on
  // logging the expense itself anymore — there is no "Insufficient /
  // Blocked" state left in this flow at all. The admin can always log a
  // construction expense, even one that pushes the client's available
  // payment balance negative; the chips below simply reflect that
  // negative number in red, matching Materials.js's Daily Material Logs.
  // =====================================================================

  // "Available to log" = everything the client has paid so far, minus
  // whatever construction expense has already been logged in earlier
  // rounds for this same invoice. This is purely INFORMATIONAL now — it
  // is allowed to go negative and never blocks or delays logging a new
  // expense.
  const availableToAllocate = allocatingInvoice ? liveAmountPaid - consumedMaterialTotal : 0;

  const allocationFormTotal = useMemo(
    () =>
      allocationRows.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.pricePerQty) || 0),
        0,
      ),
    [allocationRows],
  );

  const allocationRemaining = availableToAllocate - allocationFormTotal;

  const openAllocateModal = async (invoice) => {
    setAllocatingInvoice(invoice);
    setAllocationRows([{ ...EMPTY_ALLOCATION_ROW, id: "row-0" }]);
    setAllocationError("");
    setShowAllocateModal(true);
    setAllocationLoading(true);
    setLiveAmountPaid(Number(invoice.amountPaid) || 0);
    setConsumedMaterialTotal(0);

   try {
      const [invoiceSnap, allocSnap] = await Promise.all([
        getDoc(doc(db, "invoices", invoice.id)),
        getDoc(doc(db, "paymentAllocations", invoice.id)),
      ]);

      const allocData = allocSnap.exists() ? allocSnap.data() : null;

      setLiveAmountPaid(
        invoiceSnap.exists()
          ? Number(invoiceSnap.data().amountPaid) || 0
          : Number(invoice.amountPaid) || 0,
      );
      setConsumedMaterialTotal(getConsumedMaterialTotal(allocData));
    } catch (err) {
      console.error("Failed to load live payment/expense data:", err);
    } finally {
      setAllocationLoading(false);
    }
  };

  const closeAllocateModal = () => {
    if (allocationSaving) return;
    setShowAllocateModal(false);
    setAllocatingInvoice(null);
    setAllocationRows([{ ...EMPTY_ALLOCATION_ROW, id: "row-0" }]);
    setAllocationError("");
  };

  const addAllocationRow = () => {
    setAllocationRows((prev) => [
      ...prev,
      { ...EMPTY_ALLOCATION_ROW, id: `row-${Date.now()}-${prev.length}` },
    ]);
  };

  const removeAllocationRow = (rowId) => {
    setAllocationRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== rowId)));
  };

  const updateAllocationRow = (rowId, field, value) => {
    if ((field === "quantity" || field === "pricePerQty") && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setAllocationRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
    );
    if (allocationError) setAllocationError("");
  };

  // Logging a construction expense is NEVER blocked here — there is no
  // balance check of any kind. The admin can always record it, even if it
  // pushes the client's available payment balance negative; that is
  // simply reflected afterwards via the Remaining chip (and on
  // Materials.js's Remaining Payment indicator) in red.
  const saveAllocation = async () => {
    if (!allocatingInvoice) return;

    const validRows = allocationRows.filter((row) => row.name.trim());

    if (validRows.length === 0) {
      setAllocationError("Add at least one material item.");
      return;
    }

    if (allocationFormTotal <= 0) {
      setAllocationError("Logged total must be greater than zero.");
      return;
    }

    setAllocationSaving(true);

    const invoiceRef = doc(db, "invoices", allocatingInvoice.id);
    const allocRef = doc(db, "paymentAllocations", allocatingInvoice.id);

    try {
      await runTransaction(db, async (transaction) => {
        // All reads before any writes, per Firestore transaction rules.
        const invoiceSnap = await transaction.get(invoiceRef);

        if (!invoiceSnap.exists()) {
          throw new Error("This invoice no longer exists.");
        }

        // Tag this round with the invoiceSequence of the most recent
        // payment on file at the time of saving. Purely for display
        // grouping — not used to gate any future payment.
        const invoicePayments = Array.isArray(invoiceSnap.data().payments)
          ? invoiceSnap.data().payments
          : [];
        const latestSequence = invoicePayments.reduce(
          (max, p) => Math.max(max, getEntrySequence(p) || 0),
          0,
        );

        const entry = {
          id: `${Date.now()}`,
          invoiceSequence: latestSequence || null,
          amountAllocated: allocationFormTotal,
          createdAt: new Date().toISOString(),
          items: validRows.map((row) => ({
            name: row.name.trim(),
            quantity: Number(row.quantity) || 0,
            pricePerQty: Number(row.pricePerQty) || 0,
            total: (Number(row.quantity) || 0) * (Number(row.pricePerQty) || 0),
            supplier: row.supplier || "",
            purchaseDate: row.purchaseDate || "",
            notes: row.notes || "",
          })),
        };

        transaction.set(
          allocRef,
          {
            clientDocId: allocatingInvoice.clientDocId || "",
            invoiceId: allocatingInvoice.id,
            allocations: arrayUnion(entry),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      });

      toast.success("Construction Expense Logged Successfully");
      closeAllocateModal();
      // Refresh the map so the View / Materials pages reflect the new
      // entry immediately without requiring a full page reload.
      await fetchAllocationsMap();
    } catch (err) {
      console.error(err);
      toast.error("Failed to log construction expense.");
    } finally {
      setAllocationSaving(false);
    }
  };

  // ---------------- View Construction Expense History (read-only) ----------------
 const openViewAllocationModal = async (invoice) => {
    setViewingAllocationInvoice(invoice);
    setViewAllocations([]);
    setViewLiveAmountPaid(Number(invoice.amountPaid) || 0);
    setViewConsumedTotal(0);
    setShowViewAllocationModal(true);
    setViewAllocationLoading(true);

    try {
      const [invoiceSnap, allocSnap] = await Promise.all([
        getDoc(doc(db, "invoices", invoice.id)),
        getDoc(doc(db, "paymentAllocations", invoice.id)),
      ]);

      const allocData = allocSnap.exists() ? allocSnap.data() : null;
      const list = allocData?.allocations || [];
      // Newest entry first.
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setViewAllocations(list);
      setViewLiveAmountPaid(
        invoiceSnap.exists()
          ? Number(invoiceSnap.data().amountPaid) || 0
          : Number(invoice.amountPaid) || 0,
      );
      setViewConsumedTotal(getConsumedMaterialTotal(allocData));
    } catch (err) {
      console.error("Failed to load construction expense history:", err);
    } finally {
      setViewAllocationLoading(false);
    }
  };

  const closeViewAllocationModal = () => {
    setShowViewAllocationModal(false);
    setViewingAllocationInvoice(null);
    setViewAllocations([]);
  };

  // Kept for the "Expense Logged" chip (raw sum of this invoice's own
  // allocation rounds).
  const viewAllocatedTotal = useMemo(
    () => viewAllocations.reduce((sum, entry) => sum + (Number(entry.amountAllocated) || 0), 0),
    [viewAllocations],
  );

  // "Remaining Payment" uses the exact same live formula as the Allocate
  // modal and Materials.js (Total Payments Received − Total Material
  // Expenses Already Allocated). It is now allowed to go negative — no
  // clamping to zero — so this read-only view can never disagree with
  // either the Allocate modal or Materials.js.
  const viewRemainingPayment = viewingAllocationInvoice
    ? viewLiveAmountPaid - viewConsumedTotal
    : 0;

  // ---------------------------------------------------------------------
  // Professional View Invoice modal (Section 5) — grouped, per-invoice
  // material summary for whichever invoice is currently open in the View
  // modal. Reads the same allocationsMap already loaded for the table, so
  // opening View never triggers an extra fetch.
  //
  // Also computes `runningPaid` (cumulative amount paid up to and
  // including this payment) and `correctedBalance` (that running total
  // subtracted from the invoice's CURRENT finalEstimation) for each
  // payment, so the Invoice Timeline's "Balance" and its Generate PDF
  // button both use the corrected, always-current balance rather than
  // the stale stored `balanceAfterPayment` snapshot.
  // ---------------------------------------------------------------------
  const viewInvoiceMaterialGroups = useMemo(() => {
    if (!viewingInvoice) return [];
    const rounds = allocationsMap[viewingInvoice.id] || [];
    const payments = sortedPaymentsFor(viewingInvoice);

    let runningPaid = 0;
    return payments.map((payment) => {
      const seq = getEntrySequence(payment);
      const round = rounds.find((r) => getEntrySequence(r) === seq) || null;
      const totalUsed = round ? Number(round.amountAllocated) || 0 : 0;
      const remaining = Math.max((Number(payment.paymentAmount) || 0) - totalUsed, 0);
      runningPaid += Number(payment.paymentAmount) || 0;
      const correctedBalance = getCorrectedBalanceAfterPayment(viewingInvoice, runningPaid);
      return { payment, round, totalUsed, remaining, runningPaid, correctedBalance };
    });
  }, [viewingInvoice, allocationsMap]);

  // Payment Progress — Amount Paid as a % of the Final Quotation, driving
  // the progress bar in the View Invoice modal's Financial Summary.
  const viewPaymentProgressPct = useMemo(() => {
    if (!viewingInvoice) return 0;
    const final = Number(viewingInvoice.finalEstimation) || 0;
    const paid = Number(viewingInvoice.amountPaid) || 0;
    if (final <= 0) return 0;
    return Math.min(100, Math.round((paid / final) * 100));
  }, [viewingInvoice]);

  // =====================================================================
  // FEATURE 1 + FEATURE 2 — PDF generation.
  //
  // Both generators are strictly read-only: they reuse `invoices`,
  // `allocationsMap`, `filteredInvoices` and the existing formatting
  // helpers already defined above (formatRupees, formatDate, statusFor,
  // sortedPaymentsFor, formatMainInvoiceNumber, formatInvoiceNumberDisplay).
  // No Firestore writes, no new queries, no data duplication. Only the
  // visual presentation (layout, typography, spacing, borders) has been
  // redesigned — see drawPdfHeader / drawSectionTitle / baseTableOptions
  // above, shared identically by both PDFs.
  // =====================================================================

  // ---------------- Feature 1: Professional Individual Invoice PDF ----------------
  const generateInvoicePDF = async (invoice) => {
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 40;

      // ---- Header: Logo + Company details + Invoice meta (top) ----
      const logoDataUrl = await loadCompanyLogoDataUrl();
      const invoiceDateLabel =
        formatDate(invoice.createdAt) !== "-"
          ? formatDate(invoice.createdAt)
          : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

      let cursorY = drawPdfHeader(docPdf, {
        logoDataUrl,
        pageWidth,
        marginX,
        docTitle: "INVOICE",
        metaLines: [
          `Invoice Number: ${formatMainInvoiceNumber(invoice)}`,
          `Invoice Date: ${invoiceDateLabel}`,
          `Client ID: ${invoice.clientId || "-"}`,
        ],
      });

      // ---- Client & Project Details ----
      cursorY = drawSectionTitle(docPdf, "Client & Project Details", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        theme: "grid",
        styles: { ...baseTableOptions().styles, fontSize: 9.5 },
        showHead: false,
        body: [
          ["Client Name", invoice.clientName || "-", "Project Type", invoice.projectType || "-"],
          ["Email", invoice.email || "-", "Status", statusFor(invoice)],
          ["Address", invoice.address || "-", "Client ID", invoice.clientId || "-"],
        ],
        columnStyles: {
          0: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          1: { cellWidth: 195 },
          2: { fontStyle: "bold", textColor: colors.textMuted, cellWidth: 90 },
          3: { cellWidth: "auto" },
        },
        margin: { left: marginX, right: marginX },
      });

      cursorY = docPdf.lastAutoTable.finalY + 26;

      // ---- Payment Summary ----
      cursorY = drawSectionTitle(docPdf, "Payment Summary", marginX, cursorY);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        styles: { ...baseTableOptions().styles, halign: "center" },
        head: [[
          "Final Quotation",
          "Amount Paid",
          "Remaining Balance",
          "Total Payments",
          "Latest Payment",
          "Status",
        ]],
        body: [[
          formatRupeesPdf(invoice.finalEstimation),
          { content: formatRupeesPdf(invoice.amountPaid), styles: { textColor: colors.paidGreen, fontStyle: "bold" } },
          { content: formatRupeesPdf(invoice.balanceAmount), styles: { textColor: colors.balanceRed, fontStyle: "bold" } },
          String(Number(invoice.paymentCount) || 0),
          invoice.latestPaymentDate ? formatDate(invoice.latestPaymentDate) : "-",
          statusFor(invoice),
        ]],
        // "Estimated Amount" (originalEstimation) column removed — Final
        // Quotation is the single active quotation after Move to Invoice.
        // Widths redistributed across the remaining 515pt table width.
        columnStyles: {
          0: { cellWidth: 88 },
          1: { cellWidth: 88 },
          2: { cellWidth: 95 },
          3: { cellWidth: 68 },
          4: { cellWidth: 88 },
          5: { cellWidth: 88 },
        },
        margin: { left: marginX, right: marginX },
      });

      // Extra breathing room so Payment Summary and Payment History never
      // visually merge into one block.
      cursorY = docPdf.lastAutoTable.finalY + 30;

      // ---- Payment History ----
      const payments = sortedPaymentsFor(invoice);
      if (payments.length > 0) {
        cursorY = drawSectionTitle(docPdf, "Payment History", marginX, cursorY);

        let running = 0;
        const historyBody = payments.map((p) => {
          running += Number(p.paymentAmount) || 0;
          // Corrected balance, recomputed against the CURRENT Final
          // Quotation rather than each entry's stale stored snapshot.
          const correctedBalance = getCorrectedBalanceAfterPayment(invoice, running);
          return [
            formatInvoiceNumberDisplay(p, invoice),
            formatDate(p.paymentDate),
            { content: formatRupeesPdf(p.paymentAmount), styles: { halign: "right" } },
            { content: formatRupeesPdf(running), styles: { halign: "right" } },
            { content: formatRupeesPdf(correctedBalance), styles: { halign: "right", textColor: colors.balanceRed } },
          ];
        });

        autoTable(docPdf, {
          ...baseTableOptions(),
          startY: cursorY,
          head: [["Invoice Number", "Payment Date", "Payment Amount", "Running Total", "Remaining Balance"]],
          body: historyBody,
          columnStyles: {
            0: { cellWidth: 110 },
            1: { cellWidth: 80 },
            2: { cellWidth: 105, halign: "right" }, // widened + PDF-safe
            3: { cellWidth: 105, halign: "right" }, // "Rs." prefix so
            4: { cellWidth: 115, halign: "right" }, // amounts never clip
          },
          margin: { left: marginX, right: marginX },
        });

        cursorY = docPdf.lastAutoTable.finalY + 26;
      }

      // ---- Totals ----
      if (cursorY > 690) {
        docPdf.addPage();
        cursorY = 44;
      }

      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, cursorY, pageWidth - marginX, cursorY);
      cursorY += 20;

      autoTable(docPdf, {
        startY: cursorY,
        theme: "plain",
        styles: {
          font: PDF_THEME.fontFamily,
          fontSize: 10.5,
          cellPadding: { top: 6, bottom: 6, left: 0, right: 0 },
          fontStyle: "bold",
          textColor: colors.primaryDark,
        },
        body: [
          ["Final Project Amount", formatRupeesPdf(invoice.finalEstimation)],
          ["Total Paid", formatRupeesPdf(invoice.amountPaid)],
          ["Remaining Balance", formatRupeesPdf(invoice.balanceAmount)],
        ],
        columnStyles: { 1: { halign: "right" } },
        margin: { left: marginX, right: marginX },
      });

      // ---- Footer (every page) ----
      const pageCount = docPdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        docPdf.setPage(i);
        const pageHeight = docPdf.internal.pageSize.getHeight();
        docPdf.setFont(PDF_THEME.fontFamily, "italic");
        docPdf.setFontSize(8.5);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text("Thank you for choosing Sarrathi Constructions.", marginX, pageHeight - 30);
        docPdf.text("This invoice is system generated.", marginX, pageHeight - 18);
        docPdf.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 18, { align: "right" });
      }

      docPdf.save(`${sanitizeFileName(invoice.clientName)}.pdf`);
    } catch (err) {
      console.error("Failed to generate invoice PDF:", err);
      toast.error("Failed to generate invoice PDF.");
    }
  };

  // ---------------- Feature 2: Download All Invoices (table report) ----------------
  const generateAllInvoicesPDF = async () => {
    try {
      const { colors } = PDF_THEME;
      const docPdf = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
      const pageWidth = docPdf.internal.pageSize.getWidth();
      const marginX = 32;

      const logoDataUrl = await loadCompanyLogoDataUrl();

      let cursorY = drawPdfHeader(docPdf, {
        logoDataUrl,
        pageWidth,
        marginX,
        docTitle: "INVOICE REPORT",
        metaLines: [
          `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
          `Total Records: ${filteredInvoices.length}`,
        ],
      });

      // Same rows the on-screen table shows (respects the current search).
      const tableRows = filteredInvoices.map((inv) => [
        inv.clientId || "-",
        inv.clientName || "-",
        inv.projectType || "-",
        formatMainInvoiceNumber(inv),
        { content: formatRupeesPdf(inv.finalEstimation), styles: { halign: "right" } },
        { content: formatRupeesPdf(inv.amountPaid), styles: { halign: "right", textColor: colors.paidGreen } },
        { content: formatRupeesPdf(inv.balanceAmount), styles: { halign: "right", textColor: colors.balanceRed } },
        statusFor(inv),
        String(Number(inv.paymentCount) || 0),
        inv.latestPaymentDate ? formatDate(inv.latestPaymentDate) : "-",
      ]);

      autoTable(docPdf, {
        ...baseTableOptions(),
        startY: cursorY,
        styles: { ...baseTableOptions().styles, fontSize: 8.5 },
        head: [[
          "Client ID",
          "Client Name",
          "Project Type",
          "Invoice Number",
          "Final Quotation",
          "Amount Paid",
          "Remaining Balance",
          "Status",
          "Payments",
          "Latest Payment",
        ]],
        body: tableRows,
        // Every column gets an explicit width (sums to the 778pt table
        // width available in landscape between the margins) instead of
        // "auto" sizing on the text columns — those were squeezing the
        // currency columns too narrow, clipping digits off the right.
        columnStyles: {
          0: { cellWidth: 85 },
          1: { cellWidth: 90 },
          2: { cellWidth: 65 },
          3: { cellWidth: 100 },
          4: { cellWidth: 85, halign: "right" },
          5: { cellWidth: 85, halign: "right" },
          6: { cellWidth: 90, halign: "right" },
          7: { cellWidth: 60 },
          8: { cellWidth: 48, halign: "center" },
          9: { cellWidth: 70 },
        },
        margin: { left: marginX, right: marginX },
        didDrawPage: () => {
          const pageHeight = docPdf.internal.pageSize.getHeight();
          const pageCurrent = docPdf.internal.getNumberOfPages();
          docPdf.setFont(PDF_THEME.fontFamily, "normal");
          docPdf.setFontSize(8);
          docPdf.setTextColor(...colors.textMuted);
          docPdf.text(`Page ${pageCurrent}`, pageWidth - marginX, pageHeight - 16, { align: "right" });
        },
      });

      let footerY = docPdf.lastAutoTable.finalY + 26;
      const pageHeight = docPdf.internal.pageSize.getHeight();
      if (footerY > pageHeight - 90) {
        docPdf.addPage();
        footerY = 44;
      }

      const totalProjectValue = filteredInvoices.reduce(
        (sum, inv) => sum + (Number(inv.finalEstimation) || 0),
        0,
      );
      const totalPaid = filteredInvoices.reduce((sum, inv) => sum + (Number(inv.amountPaid) || 0), 0);
      const totalOutstandingReport = filteredInvoices.reduce(
        (sum, inv) => sum + (Number(inv.balanceAmount) || 0),
        0,
      );

      docPdf.setDrawColor(...colors.borderLight);
      docPdf.setLineWidth(0.8);
      docPdf.line(marginX, footerY, pageWidth - marginX, footerY);
      footerY += 20;

      // Four bordered summary cards instead of a single cramped bold row —
      // matches the card-style totals used on the individual invoice PDF.
      const cardWidth = (pageWidth - marginX * 2 - 30) / 4;
      const cards = [
        ["Total Clients", String(filteredInvoices.length)],
        ["Total Project Value", formatRupeesPdf(totalProjectValue)],
        ["Total Amount Paid", formatRupeesPdf(totalPaid)],
        ["Total Outstanding", formatRupeesPdf(totalOutstandingReport)],
      ];

      cards.forEach(([label, value], idx) => {
        const cardX = marginX + idx * (cardWidth + 10);
        docPdf.setDrawColor(...colors.borderLight);
        docPdf.setLineWidth(0.8);
        docPdf.roundedRect(cardX, footerY, cardWidth, 46, 4, 4);

        docPdf.setFont(PDF_THEME.fontFamily, "normal");
        docPdf.setFontSize(8);
        docPdf.setTextColor(...colors.textMuted);
        docPdf.text(label.toUpperCase(), cardX + 10, footerY + 16);

        docPdf.setFont(PDF_THEME.fontFamily, "bold");
        docPdf.setFontSize(12.5);
        docPdf.setTextColor(...colors.primaryDark);
        docPdf.text(value, cardX + 10, footerY + 34);
      });

      docPdf.save("Invoice_Report.pdf");
    } catch (err) {
      console.error("Failed to generate invoice report PDF:", err);
      toast.error("Failed to generate invoice report PDF.");
    }
  };

  return (
    <div className="page-container invoice-page">
      {/* Fixed-position notification stack — replaces alert()/window.alert() */}
      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />

      <div className="page-header">
        <div>
          <h1>Invoice</h1>
          <p>Track payments and manage active project invoices.</p>
        </div>

        <div className="invoice-summary-cards">
          <div className="invoice-summary-card">
            <span className="invoice-summary-label">Total Billed</span>
            <span className="invoice-summary-value">{formatRupees(totalBilled)}</span>
          </div>
          <div className="invoice-summary-card outstanding">
            <span className="invoice-summary-label">Outstanding Balance</span>
            <span className="invoice-summary-value">{formatRupees(totalOutstanding)}</span>
          </div>
        </div>
      </div>

      <div className="invoice-toolbar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by invoice no., name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Feature 2 — Download All Invoices as one professional table report. */}
        <button
          className="generate-report-btn"
          title="Download the complete invoice table as a PDF report"
          onClick={generateAllInvoicesPDF}
        >
          <FaFilePdf />
          Generate PDF
        </button>
      </div>

      <div className="invoice-table-card">
        <div className="invoice-table-scroll">
          <AnimatePresence mode="wait">
            <motion.table
              key={loading ? "loading" : "loaded"}
              className="invoice-table"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <thead>
                <tr>
                  <th>Invoice Number</th>
                  <th>Client Name</th>
                  <th>Email</th>
                  <th style={{ minWidth: 220 }}>Address</th>
                  <th>Project Type</th>
                  <th>Estimation</th>
                  <th>Amount Paid</th>
                  <th>Balance Amount</th>
                  <th>No. of Payments</th>
                  <th>Latest Payment</th>
                  <th>Actions</th>
                  <th>Payments</th>
                  <th>Materials</th>
                  <th>Status</th>
                  <th>PDF</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15" className="empty-row">
                      Loading invoices...
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="15" className="empty-row">
                      No Invoices Found
                    </td>
                  </tr>
                ) : (
                  paginatedInvoices.map((inv) => {
                    const status = statusFor(inv);
                    const payable = isPayable(inv);

                    return (
                      <tr key={inv.id}>
                        <td>
                          <span className="invoice-number-badge main-invoice-badge">
                            {formatMainInvoiceNumber(inv)}
                          </span>
                        </td>
                        <td>
                          {inv.clientName || "-"}
                          {getProjectRef(clientsById, inv.clientDocId) && (
                            <span style={PROJECT_REF_BADGE_STYLE}>
                              {getProjectRef(clientsById, inv.clientDocId)}
                            </span>
                          )}
                        </td>
                        <td>{inv.email || "-"}</td>
                        <td className="address-cell" style={{ minWidth: 220, whiteSpace: "normal" }}>{inv.address || "-"}</td>
                        <td>{inv.projectType || "-"}</td>
                        <td className="amount-cell">{formatRupees(inv.finalEstimation)}</td>
                        <td className="amount-cell paid-cell">{formatRupees(inv.amountPaid)}</td>
                        <td className="amount-cell balance-cell">
                          {formatRupees(inv.balanceAmount)}
                        </td>
                        <td className="amount-cell">{Number(inv.paymentCount) || 0}</td>
                        <td>
                          {inv.latestPaymentDate
                            ? formatDate(inv.latestPaymentDate)
                            : "-"}
                        </td>
                        <td>
                          <div className="action-icons">
                            <button
                              className="icon-btn view-icon"
                              title="View Invoice"
                              onClick={() => openView(inv)}
                            >
                              <FaEye />
                            </button>
                            <button
                              className="icon-btn edit-icon"
                              title="Edit Invoice"
                              onClick={() => openEdit(inv)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="icon-btn delete-icon"
                              title="Delete Invoice"
                              onClick={() => requestDeleteInvoice(inv)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="action-icons">
                            {/* Always enabled whenever a balance remains —
                                no dependency on Construction Expense
                                Allocation for any earlier payment. */}
                            <button
                              className="icon-btn add-payment-icon"
                              title={payable ? "Add Payment" : "Fully Paid"}
                              disabled={!payable}
                              onClick={() => openAddPayment(inv)}
                            >
                              <FaCoins />
                            </button>
                            <button
                              className="icon-btn payment-history-icon"
                              title="Payment History"
                              onClick={() => openPaymentHistory(inv)}
                            >
                              <FaHistory />
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="action-icons">
                            <button
                              className="icon-btn allocate-icon"
                              title="Log Construction Expense"
                              onClick={() => openAllocateModal(inv)}
                            >
                              <FaMoneyBillWave />
                            </button>
                            <button
                              className="icon-btn view-allocation-icon"
                              title="View Construction Expense History"
                              onClick={() => openViewAllocationModal(inv)}
                            >
                              <FaClipboardList />
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="status-cell">
                            <span className={`status-pill ${statusPillClass(status)}`}>
                              {status}
                            </span>
                            {isCompletable(inv) && (
                              <button
                                className="mark-completed-btn"
                                onClick={() => openCompleteModal(inv)}
                              >
                                <FaCheckCircle />
                                Mark as Completed
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {/* Feature 1 — professional individual invoice PDF,
                              downloaded as "<Client Name>.pdf". */}
                          <button
                            className="pdf-btn active-pdf-btn"
                            title="Generate PDF"
                            onClick={() => generateInvoicePDF(inv)}
                          >
                            <FaFilePdf />
                            Generate PDF
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </motion.table>
          </AnimatePresence>
        </div>
      </div>
      <Pagination {...invoicesPagination} />

      {/* ================================================================
          View Invoice Modal — Section 5: professional ERP-style layout.
          ================================================================ */}
      <AnimatePresence>
        {showViewModal && viewingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal pro-invoice-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <div className="pro-invoice-header">
                <div className="pro-invoice-header-title">
                  <FaReceipt className="pro-invoice-header-icon" />
                  <div>
                    <h2>
                      {viewingInvoice.clientName || "Client"}
                      {getProjectRef(clientsById, viewingInvoice.clientDocId) && (
                        <span style={PROJECT_REF_BADGE_STYLE}>
                          {getProjectRef(clientsById, viewingInvoice.clientDocId)}
                        </span>
                      )}
                    </h2>
                    <span className="pro-invoice-header-sub">
                      {viewingInvoice.projectType || "Project"} ·{" "}
                      {statusFor(viewingInvoice)}
                    </span>
                    <span className="invoice-number-badge main-invoice-badge pro-invoice-main-number">
                      {formatMainInvoiceNumber(viewingInvoice)}
                    </span>
                  </div>
                </div>
                <button
                  className="manage-materials-btn pro-invoice-manage-btn"
                  onClick={() => goToMaterialsFor(viewingInvoice)}
                >
                  <FaBoxOpen /> Manage Materials
                </button>
              </div>

              {/* Client Information */}
              <section className="pro-invoice-section">
                <h3><FaUserCircle className="pro-section-icon" /> Client Information</h3>
                <div className="view-grid">
                  <div className="view-field">
                    <span className="view-label">Client ID</span>
                    <span className="view-value">
                      {viewingInvoice.clientId || "-"}
                      {getProjectRef(clientsById, viewingInvoice.clientDocId) && (
                        <span style={PROJECT_REF_BADGE_STYLE}>
                          {getProjectRef(clientsById, viewingInvoice.clientDocId)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Client Name</span>
                    <span className="view-value">{viewingInvoice.clientName || "-"}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Email</span>
                    <span className="view-value">{viewingInvoice.email || "-"}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Address</span>
                    <span className="view-value">{viewingInvoice.address || "-"}</span>
                  </div>
                </div>
              </section>

              {/* Project Information */}
              <section className="pro-invoice-section">
                <h3><FaBuilding className="pro-section-icon" /> Project Information</h3>
                <div className="view-grid">
                  <div className="view-field">
                    <span className="view-label">Project Type</span>
                    <span className="view-value">{viewingInvoice.projectType || "-"}</span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Final Quotation</span>
                    <span className="view-value">
                      {formatRupees(viewingInvoice.finalEstimation)}
                    </span>
                  </div>
                  <div className="view-field">
                    <span className="view-label">Project Status</span>
                    <span className="view-value">{statusFor(viewingInvoice)}</span>
                  </div>
                  {viewingInvoice.notes && (
                    <div className="view-field view-field--wide">
                      <span className="view-label">Project Notes</span>
                      <span className="view-value">{viewingInvoice.notes}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Financial Summary — colored summary cards + progress bar.
                  Remaining Balance always shows `invoice.balanceAmount`,
                  which is already correctly kept as
                  Final Quotation - Total Amount Paid (see Payments.js's
                  saveQuotation / Invoice.js's saveAddPayment) — never the
                  Original Estimation. */}
              <section className="pro-invoice-section">
                <h3><FaWallet className="pro-section-icon" /> Financial Summary</h3>
                <div className="pro-summary-cards">
                  <div className="pro-summary-card">
                    <span className="view-label">Total Quotation</span>
                    <span className="pro-summary-value">
                      {formatRupees(viewingInvoice.finalEstimation)}
                    </span>
                  </div>
                  <div className="pro-summary-card pro-summary-card--paid">
                    <span className="view-label">Total Paid</span>
                    <span className="pro-summary-value">
                      {formatRupees(viewingInvoice.amountPaid)}
                    </span>
                  </div>
                  <div className="pro-summary-card pro-summary-card--balance">
                    <span className="view-label">Remaining Balance</span>
                    <span className="pro-summary-value">
                      {formatRupees(viewingInvoice.balanceAmount)}
                    </span>
                  </div>
                  <div className="pro-summary-card">
                    <span className="view-label">Number of Payments</span>
                    <span className="pro-summary-value">
                      {Number(viewingInvoice.paymentCount) || 0}
                    </span>
                  </div>
                </div>

                <div className="payment-progress-wrap">
                  <div className="payment-progress-labels">
                    <span className="view-label">Payment Progress</span>
                    <span className="view-value">{viewPaymentProgressPct}%</span>
                  </div>
                  <div className="payment-progress-track">
                    <div
                      className="payment-progress-fill"
                      style={{ width: `${viewPaymentProgressPct}%` }}
                    />
                  </div>
                </div>
              </section>

              {/* Invoice Timeline — Balance now uses the corrected, live
                  balance (recomputed against the CURRENT Final Quotation)
                  instead of each payment's stale stored snapshot, and
                  Generate PDF is now fully functional, reusing
                  generatePaymentEntryPDF (same branding/layout as every
                  other PDF in this file). */}
              <section className="pro-invoice-section">
                <h3><FaHistory className="pro-section-icon" /> Invoice Timeline</h3>
                {viewInvoiceMaterialGroups.length === 0 ? (
                  <div className="mb-empty">No invoices generated yet for this project.</div>
                ) : (
                  <div className="invoice-timeline-list">
                    {viewInvoiceMaterialGroups.map(({ payment, runningPaid, correctedBalance }) => {
                      const seq = getEntrySequence(payment);
                      return (
                        <div className="invoice-timeline-card" key={seq}>
                          <div className="invoice-timeline-card-main">
                            <span className="invoice-number-badge">
                              {formatInvoiceNumberDisplay(payment, viewingInvoice)}
                            </span>
                            <span className="invoice-timeline-amount">
                              {formatRupees(payment.paymentAmount)}
                            </span>
                            <span className="invoice-timeline-date">
                              {formatDate(payment.paymentDate)}
                            </span>
                          </div>
                          <div className="invoice-timeline-balance">
                            <span className="view-label">Balance</span>
                            <span className="view-value">
                              {formatRupees(correctedBalance)}
                            </span>
                          </div>
                          <button
  className="pdf-btn active-pdf-btn timeline-pdf-btn"
  title="Generate PDF for this payment"
  disabled={paymentEntryPdfGeneratingSeq === seq}
  onClick={() =>
    generatePaymentEntryPDF(viewingInvoice, payment, runningPaid, correctedBalance)
  }
  style={{
    cursor: paymentEntryPdfGeneratingSeq === seq ? "not-allowed" : "pointer",
    opacity: paymentEntryPdfGeneratingSeq === seq ? 0.6 : 1,
  }}
>
  <FaFilePdf />
  {paymentEntryPdfGeneratingSeq === seq ? "Generating..." : "Generate PDF"}
</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Purchased Material Summary — grouped by invoice */}
              <section className="pro-invoice-section">
                <h3><FaBoxOpen className="pro-section-icon" /> Purchased Material Summary</h3>
                {viewInvoiceMaterialGroups.length === 0 ? (
                  <div className="mb-empty">No material purchases recorded yet.</div>
                ) : (
                  viewInvoiceMaterialGroups.map(({ payment, round, totalUsed, remaining }) => {
                    const seq = getEntrySequence(payment);
                    return (
                      <div className="material-summary-group" key={seq}>
                        <div className="material-summary-header">
                          <span className="invoice-number-badge">
                            {formatInvoiceNumberDisplay(payment, viewingInvoice)}
                          </span>
                          <span className="material-summary-total">
                            Total Used {formatRupees(totalUsed)}
                          </span>
                          <span
                            className={`material-summary-remaining${
                              remaining > 0 ? " over-budget" : ""
                            }`}
                          >
                            Remaining {formatRupees(remaining)}
                          </span>
                        </div>
                        {!round || (round.items || []).length === 0 ? (
                          <div className="mb-empty">
                            No material allocation saved yet for this invoice.
                          </div>
                        ) : (
                          <ul className="material-summary-items">
                            {round.items.map((item, idx) => (
                              <li key={idx}>
                                <span>{item.name}</span>
                                <span>{formatRupees(item.total)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </section>

              <div className="invoice-modal-actions">
                <button className="cancel-btn" onClick={closeView}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && editingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Edit Invoice</h2>

              <div className="invoice-form-grid">
                <input
                  type="text"
                  name="clientName"
                  placeholder="Client Name"
                  value={editForm.clientName}
                  onChange={handleEditChange}
                />

                <div className="form-field-wrap">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={editForm.email}
                    onChange={handleEditChange}
                  />
                  {editErrors.email && (
                    <span className="field-error">{editErrors.email}</span>
                  )}
                </div>

                <input
                  type="text"
                  name="address"
                  placeholder="Address"
                  value={editForm.address}
                  onChange={handleEditChange}
                />

                <select
                  name="projectType"
                  value={editForm.projectType}
                  onChange={handleEditChange}
                >
                  <option value="">Select Project Type</option>
                  {PROJECT_TYPE_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field-wrap invoice-notes-field" style={{ marginBottom: 20 }}>
                <label className="move-field-label">Project Notes (optional)</label>
                <textarea
                  name="notes"
                  rows={3}
                  placeholder="Internal notes about this project..."
                  value={editForm.notes}
                  onChange={handleEditChange}
                />
              </div>

              <div className="form-field-wrap" style={{ marginBottom: 16 }}>
                <label className="move-field-label">Final Quotation</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="finalEstimation"
                  placeholder="Final Quotation (₹)"
                  value={editForm.finalEstimation}
                  onChange={handleEditFinalEstimationChange}
                />
                {editErrors.finalEstimation && (
                  <span className="field-error">{editErrors.finalEstimation}</span>
                )}
              </div>

              <div className="balance-display invoice-edit-balance">
                <span className="view-label">Amount Paid So Far</span>
                <span className="balance-amount-value small">
                  {formatRupees(editingInvoice.amountPaid)}
                </span>
              </div>

              <div className="balance-display invoice-edit-balance">
                <span className="view-label">Current Balance (from payment history)</span>
                <span className="balance-amount-value">
                  {formatRupees(editingInvoice.balanceAmount)}
                </span>
              </div>

              <div className="invoice-modal-actions">
                <button className="cancel-btn" onClick={closeEdit}>
                  Cancel
                </button>

                <button className="save-btn" disabled={saving} onClick={saveEdit}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Project Completion Confirmation Modal */}
      <AnimatePresence>
        {showCompleteModal && completingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal complete-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Project Completion</h2>
              <p className="complete-modal-question">
                Has the project for <strong>{completingInvoice.clientName || "this client"}</strong>{" "}
                been successfully completed?
              </p>

              <div className="balance-display invoice-edit-balance">
                <span className="view-label">Balance Amount</span>
                <span className="balance-amount-value">
                  {formatRupees(completingInvoice.balanceAmount)}
                </span>
              </div>

              <div className="invoice-modal-actions">
                <button
                  className="cancel-btn"
                  disabled={completingSaving}
                  onClick={closeCompleteModal}
                >
                  Cancel
                </button>

                <button
                  className="save-btn"
                  disabled={completingSaving}
                  onClick={confirmMarkCompleted}
                >
                  {completingSaving ? "Saving..." : "Mark as Completed"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Payment Modal — recording a payment here generates a
          brand-new, permanent child invoice under this project's Main
          Invoice Number. Available any time the project has a balance;
          there is no requirement to log Construction Expense Allocation
          for any earlier payment first. */}
      <AnimatePresence>
        {showAddPaymentModal && payingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal add-payment-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Add Payment — {payingInvoice.clientName || "Client"}</h2>
              <p className="allocation-hint">
                This will generate a new permanent payment invoice under{" "}
                <strong>{formatMainInvoiceNumber(payingInvoice, "this project's invoice number")}</strong>{" "}
                and reduce the balance automatically. Earlier invoices are never changed,
                and you can record this payment any time — logging Construction Expense
                Allocation for a previous payment is not required first.
              </p>

              <div className="view-grid move-invoice-summary">
                <div className="view-field">
                  <span className="view-label">Final Quotation</span>
                  <span className="view-value">
                    {formatRupees(payingInvoice.finalEstimation)}
                  </span>
                </div>
                <div className="view-field">
                  <span className="view-label">Current Balance</span>
                  <span className="view-value">
                    {formatRupees(payingInvoice.balanceAmount)}
                  </span>
                </div>
              </div>

              <div className="invoice-form-grid">
                <div className="form-field-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Payment Amount"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    autoFocus
                  />
                </div>

                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => {
                    setPaymentDate(e.target.value);
                    if (paymentError) setPaymentError("");
                  }}
                />
              </div>

              <div className="balance-display invoice-edit-balance">
                <span className="view-label">Balance After This Payment</span>
                <span className="balance-amount-value">
                  {formatRupees(paymentPreviewBalance)}
                </span>
              </div>

              {paymentError && (
                <span className="field-error materials-form-error">{paymentError}</span>
              )}

              <div className="invoice-modal-actions">
                <button className="cancel-btn" disabled={paymentSaving} onClick={closeAddPayment}>
                  Cancel
                </button>
                <button className="save-btn" disabled={paymentSaving} onClick={saveAddPayment}>
                  {paymentSaving ? "Saving..." : "Save Payment"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment History Modal — redesigned as professional invoice cards,
          each carrying its permanent child invoice number
          ("{mainInvoiceNumber}-01", "-02", "-03"...). Payment Date & Notes
          are editable inline (Section 6); Invoice Number, Amount, and
          Balance are permanently locked. Each card's Generate PDF button
          produces a receipt for that single payment only. Balance Remaining
          now uses the corrected, live-recomputed value instead of the
          stale stored snapshot. */}
      <AnimatePresence>
        {showPaymentHistoryModal && historyInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal allocation-modal payment-history-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>
                Payment History — {historyInvoice.clientName || "Client"}
                {getProjectRef(clientsById, historyInvoice.clientDocId) && (
                  <span style={PROJECT_REF_BADGE_STYLE}>
                    {getProjectRef(clientsById, historyInvoice.clientDocId)}
                  </span>
                )}
              </h2>

              <div className="allocation-summary">
                <div className="allocation-chip">
                  <span className="view-label">Main Invoice Number</span>
                  <span className="balance-amount-value small">
                    {formatMainInvoiceNumber(historyInvoice)}
                  </span>
                </div>
                <div className="allocation-chip">
                  <span className="view-label">Final Quotation</span>
                  <span className="balance-amount-value small">
                    {formatRupees(historyInvoice.finalEstimation)}
                  </span>
                </div>
                <div className="allocation-chip">
                  <span className="view-label">Total Amount Paid</span>
                  <span className="balance-amount-value small">
                    {formatRupees(historyInvoice.amountPaid)}
                  </span>
                </div>
                <div className="allocation-chip">
                  <span className="view-label">Current Balance</span>
                  <span className="balance-amount-value small">
                    {formatRupees(historyInvoice.balanceAmount)}
                  </span>
                </div>
              </div>

              {historyPaymentsWithRunning.length === 0 ? (
                <div className="mb-empty">No payments recorded yet for this project.</div>
              ) : (
                <div className="payment-cards-grid">
                  {historyPaymentsWithRunning.map(({ entry, runningTotal, correctedBalance }) => {
                    const seq = getEntrySequence(entry);
                    const isEditing = editingHistorySeq === seq;
                    const isFinal = correctedBalance <= 0;

                    return (
                      <div className="payment-card" key={seq}>
                        <div className="payment-card-top">
                          <span className="invoice-number-badge payment-card-number">
                            {formatInvoiceNumberDisplay(entry, historyInvoice)}
                          </span>
                          <span
                            className={`payment-status-pill${isFinal ? " is-final" : ""}`}
                          >
                            {isFinal ? "Fully Paid" : "Paid"}
                          </span>
                        </div>

                        <div className="payment-card-grid">
                          <div className="payment-card-field">
                            <span className="view-label">Payment Date</span>
                            {isEditing ? (
                              <input
                                type="date"
                                className="payment-history-inline-input"
                                value={historyEditForm.paymentDate}
                                onChange={(e) =>
                                  setHistoryEditForm((prev) => ({
                                    ...prev,
                                    paymentDate: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="view-value">{formatDate(entry.paymentDate)}</span>
                            )}
                          </div>

                          <div className="payment-card-field">
                            <span className="view-label">Payment Amount</span>
                            <span className="view-value payment-card-amount">
                              {formatRupees(entry.paymentAmount)}
                            </span>
                          </div>

                          <div className="payment-card-field">
                            <span className="view-label">Running Total Paid</span>
                            <span className="view-value">{formatRupees(runningTotal)}</span>
                          </div>

                          <div className="payment-card-field">
                            <span className="view-label">Balance Remaining</span>
                            <span className="view-value">
                              {formatRupees(correctedBalance)}
                            </span>
                          </div>

                          <div className="payment-card-field payment-card-field--wide">
                            <span className="view-label">Payment Notes</span>
                            {isEditing ? (
                              <input
                                type="text"
                                className="payment-history-inline-input"
                                placeholder="Optional note"
                                value={historyEditForm.paymentNotes}
                                onChange={(e) =>
                                  setHistoryEditForm((prev) => ({
                                    ...prev,
                                    paymentNotes: e.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="view-value">{entry.paymentNotes || "-"}</span>
                            )}
                          </div>
                        </div>

                        <div className="payment-card-actions">
                          {isEditing ? (
                            <>
                              <button
                                className="cancel-btn small"
                                disabled={historyEditSaving}
                                onClick={cancelEditHistoryEntry}
                              >
                                Cancel
                              </button>
                              <button
                                className="save-btn small"
                                disabled={historyEditSaving}
                                onClick={saveEditHistoryEntry}
                              >
                                {historyEditSaving ? "Saving..." : "Save"}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="icon-btn edit-icon"
                                title="Edit Payment Date / Notes"
                                onClick={() => openEditHistoryEntry(entry)}
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="pdf-btn active-pdf-btn"
                                title="Download a PDF receipt for this payment"
                                disabled={paymentEntryPdfGeneratingSeq === seq}
                                onClick={() =>
                                  generatePaymentEntryPDF(historyInvoice, entry, runningTotal, correctedBalance)
                                }
                                style={{
                                  cursor: paymentEntryPdfGeneratingSeq === seq ? "not-allowed" : "pointer",
                                  opacity: paymentEntryPdfGeneratingSeq === seq ? 0.6 : 1,
                                }}
                              >
                                <FaFilePdf />
                                {paymentEntryPdfGeneratingSeq === seq ? "Generating..." : "Generate PDF"}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="invoice-modal-actions">
                <button className="cancel-btn" onClick={closePaymentHistory}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log Construction Expense Modal (formerly "Allocate Current
          Payment"). Records how money already paid has been spent on
          construction/materials. Purely a log — it is never a
          prerequisite for the client's next payment, and it is NEVER
          blocked by an insufficient available balance either. */}
      <AnimatePresence>
        {showAllocateModal && allocatingInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal allocation-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Construction Expense Allocation</h2>
              <p className="allocation-hint">
                Log how funds already paid by this client have been spent on
                day-to-day construction expenses/materials. This does not change
                Estimation's Budget Plan, and it is no longer required before
                recording the client's next payment — it simply keeps a running
                expense record that Materials reports against. Logging is never
                blocked, even if it pushes Remaining past zero — a negative
                Remaining just means expenses have outpaced payments received so
                far.
              </p>

              {allocationLoading ? (
                <div className="mb-empty">Loading payment details...</div>
              ) : (
                <>
                  <div className="allocation-summary">
                    <div
                      className={`allocation-chip${availableToAllocate < 0 ? " over-budget" : ""}`}
                    >
                      <span className="view-label">Available Payment</span>
                      <span className="balance-amount-value small">
                        {formatSignedRupees(availableToAllocate)}
                      </span>
                    </div>
                    <div className="allocation-chip">
                      <span className="view-label">Logged Now</span>
                      <span className="balance-amount-value small">
                        {formatRupees(allocationFormTotal)}
                      </span>
                    </div>
                    <div
                      className={`allocation-chip${allocationRemaining < 0 ? " over-budget" : ""}`}
                    >
                      <span className="view-label">Remaining</span>
                      <span className="balance-amount-value small">
                        {formatSignedRupees(allocationRemaining)}
                      </span>
                    </div>
                  </div>

                  <div className="allocation-rows">
                    {allocationRows.map((row) => {
                      const rowTotal =
                        (Number(row.quantity) || 0) * (Number(row.pricePerQty) || 0);
                      return (
                        <div className="allocation-row" key={row.id}>
                          <input
                            type="text"
                            placeholder="Material Name"
                            value={row.name}
                            onChange={(e) => updateAllocationRow(row.id, "name", e.target.value)}
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="Quantity"
                            value={row.quantity}
                            onChange={(e) =>
                              updateAllocationRow(row.id, "quantity", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="Price / Qty (₹)"
                            value={row.pricePerQty}
                            onChange={(e) =>
                              updateAllocationRow(row.id, "pricePerQty", e.target.value)
                            }
                          />
                          <span className="allocation-row-total">{formatRupees(rowTotal)}</span>
                          <input
                            type="text"
                            placeholder="Supplier"
                            value={row.supplier}
                            onChange={(e) =>
                              updateAllocationRow(row.id, "supplier", e.target.value)
                            }
                          />
                          <input
                            type="date"
                            value={row.purchaseDate}
                            onChange={(e) =>
                              updateAllocationRow(row.id, "purchaseDate", e.target.value)
                            }
                          />
                          <input
                            type="text"
                            placeholder="Notes"
                            value={row.notes}
                            onChange={(e) => updateAllocationRow(row.id, "notes", e.target.value)}
                          />
                          <button
                            className="icon-btn delete-icon allocation-row-remove"
                            title="Remove Row"
                            onClick={() => removeAllocationRow(row.id)}
                            disabled={allocationRows.length === 1}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button className="add-row-btn" onClick={addAllocationRow}>
                    + Add Item
                  </button>

                  {allocationError && (
                    <span className="field-error materials-form-error">{allocationError}</span>
                  )}
                </>
              )}

              <div className="invoice-modal-actions">
                <button
                  className="cancel-btn"
                  disabled={allocationSaving}
                  onClick={closeAllocateModal}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  disabled={allocationSaving || allocationLoading}
                  onClick={saveAllocation}
                >
                  {allocationSaving ? "Saving..." : "Save Expense Log"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Construction Expense History Modal (read-only) — formerly
          "View Current Payment Allocation". */}
      <AnimatePresence>
        {showViewAllocationModal && viewingAllocationInvoice && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal allocation-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Construction Expense History — {viewingAllocationInvoice.clientName || "Client"}</h2>

              {viewAllocationLoading ? (
                <div className="mb-empty">Loading construction expense history...</div>
              ) : (
                <>
                  <div className="allocation-summary">
                    <div className="allocation-chip">
                      <span className="view-label">Expense Logged</span>
                      <span className="balance-amount-value small">
                        {formatRupees(viewAllocatedTotal)}
                      </span>
                    </div>
                    <div
                      className={`allocation-chip${viewRemainingPayment < 0 ? " over-budget" : ""}`}
                    >
                      <span className="view-label">Remaining Payment</span>
                      <span className="balance-amount-value small">
                        {formatSignedRupees(viewRemainingPayment)}
                      </span>
                    </div>
                  </div>

                  {viewAllocations.length === 0 ? (
                    <div className="mb-empty">No construction expense logged yet.</div>
                  ) : (
                    <div className="allocation-history">
                      {viewAllocations.map((entry) => (
                        <div className="allocation-history-block" key={entry.id}>
                          <div className="allocation-history-head">
                            <span>{formatDate(entry.createdAt)}</span>
                            <span>{formatRupees(entry.amountAllocated)}</span>
                          </div>
                          <div className="allocation-rows read-only">
                            <div className="allocation-row allocation-row-head">
                              <span>Material</span>
                              <span>Qty</span>
                              <span>Price/Qty</span>
                              <span>Total</span>
                              <span>Supplier</span>
                              <span>Date</span>
                              <span>Notes</span>
                            </div>
                            {(entry.items || []).map((item, idx) => (
                              <div className="allocation-row" key={idx}>
                                <span>{item.name}</span>
                                <span>{item.quantity}</span>
                                <span>{formatRupees(item.pricePerQty)}</span>
                                <span>{formatRupees(item.total)}</span>
                                <span>{item.supplier || "-"}</span>
                                <span>{item.purchaseDate || "-"}</span>
                                <span>{item.notes || "-"}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="invoice-modal-actions">
                <button className="cancel-btn" onClick={closeViewAllocationModal}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

{/* Manage Project modal — replaces the old immediate-delete flow. */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            className="invoice-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="invoice-page-modal delete-options-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <h2>Manage Project — {deleteTarget.clientName || "Client"}</h2>
              <p className="allocation-hint">
                Choose what to do with this project. On Hold and Back to
                Estimation keep every record intact. Delete Permanently only
                marks the client as Rejected, exactly like elsewhere in the app.
              </p>

              <div className="delete-options-list">
                <button
                  className="delete-option-btn onhold-option"
                  disabled={manageActionBusy}
                  onClick={handleMoveToOnHold}
                >
                  <FaPauseCircle />
                  {onHoldSaving ? "Moving..." : "Move to On Hold"}
                </button>

                <button
                  className="delete-option-btn estimation-option"
                  disabled={manageActionBusy}
                  onClick={handleMoveBackToEstimation}
                >
                  <FaUndoAlt />
                  {backToEstimationSaving ? "Moving..." : "Move Back to Estimation"}
                </button>

                <button
                  className="delete-option-btn danger-option"
                  disabled={manageActionBusy}
                  onClick={handleRejectPermanently}
                >
                  <FaTrash />
                  {rejectSaving ? "Processing..." : "Delete Permanently"}
                </button>
              </div>

              <div className="invoice-modal-actions">
                <button
                  className="cancel-btn"
                  disabled={manageActionBusy}
                  onClick={cancelDeleteInvoice}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Invoice;